import { queryKeysInvalidatedByBenefitDeclaration } from "@/pages/accountant/benefitDeclarationInvalidations";
import {
  ArrowLeft,
  ArrowRight,
  BadgePercent,
  Check,
  ChevronDown,
  Clock,
  Copy,
  CreditCard,
  FileText,
  KeyRound,
  Loader2,
  MapPin,
  MessageCircle,
  Package,
  Receipt,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  User,
  UserCheck,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  FiscalBenefitDeclaration,
  isFiscalBenefitDeclarationComplete,
  type FiscalBenefitDeclarationValue,
} from "@/components/features/fiscal-benefit/FiscalBenefitDeclaration";
import {
  getFiscalBenefitDeclarationForTenant,
  recordFiscalBenefitDeclarationForTenant,
} from "@/services/fiscalService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AccountantHomologacionSimplificadaFlow } from "@/pages/accountant/AccountantHomologacionSimplificadaFlow";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { PlanCardGrid } from "@/components/onboarding/PlanCardGrid";
import {
  BankTransferPanel,
  grossWithIva,
} from "@/components/features/accountant/BankTransferPanel";
import { CertificateUploadForm } from "@/components/shared/CertificateUploadForm";
import { cn } from "@/lib/utils";
import { formatDisplayDate } from "@/lib/dateFormat";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { dashboardKeys, tenantKeys } from "@/lib/queryKeys";
import {
  ONBOARDING_NUDGE_ACCOUNTANT_INVITE,
  ONBOARDING_NUDGE_ACCT_CAE_LOADED,
  ONBOARDING_NUDGE_ACCT_CFE_HISTORY_IMPORTED,
  ONBOARDING_NUDGE_ACCT_FISCAL_PROFILE_CONFIRMED,
  ONBOARDING_NUDGE_ACCT_FISCALO_CONFIG_CONFIRMED,
  ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED,
  ONBOARDING_NUDGE_ACCT_TRANSFER_RESOLVED,
} from "@/hooks/useOnboardingPending";
import {
  dismissDashboardAlert,
  getDashboard,
} from "@/services/dashboardService";
import {
  completeOnboarding,
  confirmDgiPortalUser,
  createDgiPortalUser,
  getTenantCurrent,
  getTenantPlans,
  updateTenant,
  updateTenantBranding,
  updateTenantPlan,
  updateTenantSucursal,
  uploadCurrentTenantImage,
  type InvoiceLogoCropPayload,
} from "@/services/authService";
import type { TenantCurrentResponse } from "@/services/authService";
import { CompanyImageCard } from "@/components/features/company-image/CompanyImageCard";
import { FiscalProfileStep, type FiscalProfileStepRef } from "@/pages/onboarding/FiscalProfileStep";
import { TransferOwnershipCard } from "@/pages/users/TransferOwnershipCard";
import { listTenantMembers } from "@/services/tenantUsersService";
import {
  cancelPlanCheckoutForTenant,
  createBankTransferCheckoutForTenant,
  createBankTransferIntentForTenant,
  createPlanCheckoutForTenant,
  getPendingPlanCheckoutForTenant,
} from "@/services/planPaymentsService";
import { isFreePlan } from "@/components/shared/PaidPlanRoute";
import {
  importEmittedCfes,
  importRecibidasCfes,
} from "@/services/invoiceService";
import {
  getFiscaloConfigRunning,
  requestProveedorSoftwareRegistration,
  startFiscaloConfig,
} from "@/services/fiscalService";
import { getIngresoSimplificado } from "@/services/ingresoSimplificadoService";
import { AddCaeDialog } from "@/pages/settings/AddCaeDialog";
import { ImportEmittedCfeDialog } from "@/pages/tenants/ImportEmittedCfeDialog";
import { ImportRecibidasCfeDialog } from "@/pages/tenants/ImportRecibidasCfeDialog";
import { tenantPlanPrice } from "@/lib/tenant-plan-pricing";
import { selectVisiblePlans } from "@/pages/accountant/visiblePlans";

// Single source of truth for what the accountant must enter when
// creating the DGI e-Factura user. Matches the same constant in the
// self-service onboarding (DgiAccessStep) — keep them in sync.
const DGI_NOTIFICATIONS_EMAIL = "notificaciones-dgi@fiscalo.uy";

export function AccountantClientOnboardingPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { user, refreshUser } = useAuth();
  const { activeTenant, switchTenant, isSwitchingTenant } = useTenantSwitcher();
  const [startingAutoFlow, setStartingAutoFlow] = useState(false);
  const [savingFiscaloConfig, setSavingFiscaloConfig] = useState(false);
  // Tracks whether the in-flight check has ever confirmed the chain is
  // actually running since the user clicked Configurar ahora. We can't
  // fire the "stalled" toast on the very first poll after the click
  // (the request to Python may not have created the session yet, so the
  // poll returns running:false and we'd race ourselves). Flipping this
  // to true once we observe a real running session — and resetting it
  // whenever savingFiscaloConfig drops — gates the stalled effect.
  const sawFiscaloRunningRef = useRef(false);

  // Step 1 (Usuario e-Factura DGI) state. The accountant flow only
  // supports the "generate" path — we control the notification email
  // and the assigned roles, so importing a pre-existing DGI user would
  // bypass those guarantees. The backend generates a fresh password
  // tied to the accountant's CI and returns it once for them to copy.
  const [savingPortalUser, setSavingPortalUser] = useState(false);
  const [confirmingPortalUser, setConfirmingPortalUser] = useState(false);
  const [generatedPortalUserPassword, setGeneratedPortalUserPassword] =
    useState<string | null>(null);
  // One-shot guard: the very first time the section becomes actionable
  // we auto-fire the password generation so the accountant doesn't have
  // to tap "Generar contraseña" just to see the credentials they need.
  const generateAutoFiredRef = useRef(false);

  // Step "Sucursal" state. Input is text so we can validate strictly — number
  // input fields accept exponents and silent NaN. Backend enforces 1–99 too.
  const [sucursalInput, setSucursalInput] = useState("");
  const [savingSucursal, setSavingSucursal] = useState(false);
  // Once the sucursal step is "Listo" the card collapses to a summary. Pressing
  // it re-opens the editor so the accountant can review/change the code (the
  // default 1 fits almost everyone, but migrated clients may differ).
  const [sucursalExpanded, setSucursalExpanded] = useState(false);

  // 3-phase wizard. Phase 1 = current DGI checklist. Phase 2 = Fiscalo
  // company configuration (public name / logo / brand color / fiscal
  // profile). Phase 3 = ownership transfer to the real owner. The
  // initial render lands on whichever phase is still incomplete.
  // 4-phase wizard:
  //   1 = DGI checklist (credentials, cert, CAEs, history, idemisor)
  //   2 = Fiscalo branding (public name, logo, brand color)
  //   3 = Perfil fiscal (company type, IVA regime + minimum benefit, IRAE, ...)
  //   4 = Propiedad (ownership transfer)
  // Phases 2 and 3 used to be one step; we split them because the fiscal
  // profile is dense enough to merit its own panel.
  const [phase, setPhase] = useState<1 | 2 | 3 | 4>(1);
  const initialPhaseSetRef = useRef(false);

  // Phase 2 form state.
  const [publicNameInput, setPublicNameInput] = useState("");
  const [savingPublicName, setSavingPublicName] = useState(false);
  const [useSameLogo, setUseSameLogo] = useState(true);
  const [companyImageUploading, setCompanyImageUploading] = useState(false);
  const [siteLogoUploading, setSiteLogoUploading] = useState(false);
  const [invoiceLogoUploading, setInvoiceLogoUploading] = useState(false);
  const [brandColorSaving, setBrandColorSaving] = useState(false);
  const [invoiceLogoCropSaving, setInvoiceLogoCropSaving] = useState(false);
  const [savingBenefitDeclaration, setSavingBenefitDeclaration] =
    useState(false);
  const [confirmingFiscaloPanel, setConfirmingFiscaloPanel] = useState(false);
  const [confirmingFiscalProfilePanel, setConfirmingFiscalProfilePanel] =
    useState(false);
  const fiscalProfileRef = useRef<FiscalProfileStepRef>(null);
  const [fiscalProfileValid, setFiscalProfileValid] = useState(false);

  // Phase 3 state.
  const [resolvingTransfer, setResolvingTransfer] = useState(false);

  const [caeDialogOpen, setCaeDialogOpen] = useState(false);
  const [cfeEmittedDialogOpen, setCfeEmittedDialogOpen] = useState(false);
  const [cfeRecibidasDialogOpen, setCfeRecibidasDialogOpen] = useState(false);

  // Plan + payment step state. Source-of-truth for both "is there an
  // active pending link" and "did the accountant resolve the plan
  // already" is the backend now — no more per-browser localStorage.
  // pendingCheckout: GET /tenants/current/pending-plan-checkout.
  // planResolved:    tenant.planResolvedAt (set whenever the user
  //                  activates ANY plan, free or paid).
  const [planChoice, setPlanChoice] = useState<{
    planId: string;
    interval: "monthly" | "annual";
  } | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  // Bank-transfer sub-flow (annual plans only): once the accountant opens
  // the panel they see the Salkantai account details, then upload the wire
  // receipt to activate the plan on the spot.
  const [bankTransferOpen, setBankTransferOpen] = useState(false);
  /**
   * The checkout the client will wire against, opened when the panel is
   * expanded so the wizard can show the payment reference. Per-tenant (id from
   * the URL), like the submit below.
   */
  const [transferCheckoutId, setTransferCheckoutId] = useState<string | null>(
    null,
  );
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  // Accountant's Literal E (IVA mínimo) declaration for the plan step. Held in
  // LOCAL state so toggling is instant — we do NOT hit the API on every click.
  // The choice is persisted once, at plan activation (see persistLiteralEIfNeeded).
  const [literalEChoice, setLiteralEChoice] = useState<boolean | null>(null);
  const literalESeededRef = useRef(false);
  // Decreto 206/019 benefit declaration, held locally and persisted on the same
  // beat as literalEChoice (see persistFiscalBenefitIfNeeded). It lives on the
  // plan step because the declared motivo decides whether this client is offered
  // discounted pricing — asking after they picked a plan would be too late.
  const [benefitDeclaration, setBenefitDeclaration] =
    useState<FiscalBenefitDeclarationValue>({ complies: null, motivo: null });
  const benefitSeededRef = useRef(false);

  // Land the session on the URL-pinned tenant so all downstream queries
  // (getTenantCurrent, certificate upload, auto-flow start) target the
  // right company. The session→URL sync in TenantSwitcherContext skips
  // /accountant paths, so we drive the switch here.
  //
  // silent: true because the accountant just clicked into this client —
  // the page change is the feedback; a "Cambiaste a X" toast on top of
  // that is noise. Also dodges a Strict Mode double-fire in dev: the
  // isSwitchingTenant guard doesn't latch synchronously, so without
  // silent both effect runs would each emit the toast.
  useEffect(() => {
    if (!tenantId) return;
    if (activeTenant?.id === tenantId) return;
    if (isSwitchingTenant) return;
    void switchTenant(tenantId, { silent: true }).catch(() => {
      toast.error(
        t(
          "accountantClientOnboarding.switchError",
          "No pudimos abrir esta empresa.",
        ),
      );
      navigate("/accountant", { replace: true });
    });
  }, [
    tenantId,
    activeTenant?.id,
    isSwitchingTenant,
    switchTenant,
    navigate,
    t,
  ]);

  const sessionMatches = Boolean(
    tenantId && activeTenant?.id === tenantId,
  );

  const queryClient = useQueryClient();

  const {
    data: tenant,
    refetch: refetchTenant,
  } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: sessionMatches && Boolean(user?.tenantId),
    // Poll tenant every 5s while the merged Fiscalo-config automation is
    // in flight so the UI flips to "Listo" + toast without the accountant
    // having to refresh. The actual idemisor_sync → proveedor_software
    // chain typically takes 1-3 minutes; idle queries stay on demand.
    refetchInterval: () => (savingFiscaloConfig ? 5_000 : false),
  });

  // Homologación-simplificada tenants ("scratch"): while the ingreso flow is
  // in progress the page renders the dedicated homologación wizard (branch
  // below). Once it's `completed`, the emisor is live in DGI with Fiscalo
  // already registered as proveedor — so we fall through to the STANDARD
  // wizard to collect the remaining setup (certificate, CAE, sucursal, plan…),
  // hiding the two phase-1 steps homologación already covered: the e-Factura
  // user and the "Configuración Fiscalo en DGI" (proveedor) step.
  const { data: ingresoSimplificado, refetch: refetchIngreso } = useQuery({
    queryKey: ["ingreso-simplificado"] as const,
    queryFn: getIngresoSimplificado,
    enabled:
      sessionMatches &&
      tenant?.onboardingSituationChoice === "scratch",
  });
  // `completed` = DGI granted the producción role (upload cert/CAE/plan now);
  // `vigente` = DGI's padrón confirmed the emisor is active (emission unlocked).
  // Both fall through to the standard wizard, so treat either as "homologado".
  const ingresoCompleted =
    ingresoSimplificado?.status === "completed" ||
    ingresoSimplificado?.status === "vigente";
  // Emisor is confirmed active in DGI's padrón only once `vigente`. Between
  // `completed` and `vigente` the RUC cannot invoice yet (Fecha Vigencia rule).
  const emisorVigente = ingresoSimplificado?.status === "vigente";
  const fechaVigencia = ingresoSimplificado?.fechaVigencia ?? null;
  const homologado =
    tenant?.onboardingSituationChoice === "scratch" && ingresoCompleted;

  // Gate the fall-through from the homologación wizard to the standard wizard.
  // We must NOT auto-advance the instant `ingresoCompleted` flips: the flow
  // polls this same query, so completion would silently yank the accountant
  // off the "Homologación completa" panel mid-glance. Instead:
  //   · If homologación was ALREADY completed when this page mounted (a
  //     returning accountant), skip straight to the standard wizard.
  //   · If it completes DURING this session, keep showing the flow's completed
  //     panel with its "Continuar" button, and only advance when they click it.
  const [homologacionContinued, setHomologacionContinued] = useState(false);
  const ingresoCompletedOnMountRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (ingresoSimplificado == null) return;
    if (ingresoCompletedOnMountRef.current !== null) return;
    ingresoCompletedOnMountRef.current = ingresoCompleted;
  }, [ingresoSimplificado, ingresoCompleted]);
  const skipHomologacionWizard =
    ingresoCompletedOnMountRef.current === true || homologacionContinued;
  const handleContinuePastHomologacion = (): void => {
    // Refetch so `ingresoCompleted` (→ `homologado`) is fresh before the
    // standard wizard mounts — that flag hides the steps homologación covered.
    void refetchIngreso();
    setHomologacionContinued(true);
  };

  // Source of truth for "accountant marked CAEs uploaded" / "accountant
  // marked CFE history imported" lives in company_dismissed_dashboard_alert
  // (tenant-scoped), so a different device or coworker sees the same state.
  // Same plumbing the dashboard onboarding card uses for its nudges.
  const { data: dashboardData } = useQuery({
    queryKey: dashboardKeys.metrics(),
    queryFn: getDashboard,
    enabled: sessionMatches,
  });
  const dismissedAlertIds = dashboardData?.dismissedAlertIds ?? [];

  // Re-hydrate the "Configurando…" spinner after a page refresh by
  // asking the backend whether either idemisor_sync or proveedor_software
  // is still in-flight in the session store. Refetch interval matches
  // the tenant poll cadence so completion clears it within ~5s.
  const { data: fiscaloConfigRunning } = useQuery({
    queryKey: ["fiscalo-config-running", tenantId] as const,
    queryFn: getFiscaloConfigRunning,
    enabled: sessionMatches,
    refetchInterval: () => (savingFiscaloConfig ? 5_000 : 30_000),
  });
  useEffect(() => {
    if (fiscaloConfigRunning?.running) {
      sawFiscaloRunningRef.current = true;
      if (!savingFiscaloConfig) {
        setSavingFiscaloConfig(true);
      }
    }
  }, [fiscaloConfigRunning?.running, savingFiscaloConfig]);
  useEffect(() => {
    if (!savingFiscaloConfig) {
      // Click released; reset so the next run starts fresh.
      sawFiscaloRunningRef.current = false;
    }
  }, [savingFiscaloConfig]);
  // Stalled-detection effect lives further down — it needs
  // fiscaloConfigDone / fiscaloConfigManualReview which are derived a
  // few hundred lines below. Declaring the effect here would dereference
  // them in TDZ and crash with "Cannot access X before initialization".

  // Phase 3 detection: do we have a non-accountant owner already? Pulled
  // here (vs only on Phase 3 mount) so the stepper can show phase 3 done
  // without the accountant having to click around.
  const { data: tenantMembers = [], refetch: refetchMembers } = useQuery({
    queryKey: ["accountant-onboarding-members", tenantId] as const,
    queryFn: () => listTenantMembers(tenantId!),
    enabled: sessionMatches && tenantId != null,
  });
  // The nudge is a self-declaration ("I've finished adding the CAE types"),
  // NOT evidence that any CAE exists. Both are required, because the backend
  // activation guard counts real rows: a tenant with the nudge set and zero
  // CAEs was shown "CAEs · LISTO" here while activation refused for exactly
  // that step, leaving the accountant with a checklist and an error that
  // contradicted each other (ZEIDA ROSENBLATT, reported 2026-09-09).
  //
  // Not `caeCount > 0` alone either: a tenant loads several ranges
  // (e-Factura, e-Ticket, NC/ND) and the step must not self-complete after the
  // first one.
  const caeCount = dashboardData?.caeCount ?? 0;
  const caeMarkedDone =
    dismissedAlertIds.includes(ONBOARDING_NUDGE_ACCT_CAE_LOADED) &&
    caeCount > 0;
  const cfeMarkedDone = dismissedAlertIds.includes(
    ONBOARDING_NUDGE_ACCT_CFE_HISTORY_IMPORTED,
  );

  const markStepMutation = useMutation({
    mutationFn: (alertId: string) => dismissDashboardAlert(alertId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
    },
    onError: () => {
      toast.error(
        t(
          "accountantClientOnboarding.markStepError",
          "No pudimos guardar el cambio. Probá de nuevo.",
        ),
      );
    },
  });
  // The mutation is shared by CAE + CFE buttons; useMutation.variables holds
  // the alertId of the in-flight call, so each button can show its own
  // spinner without us spinning up two separate hooks.
  const markingCae =
    markStepMutation.isPending &&
    markStepMutation.variables === ONBOARDING_NUDGE_ACCT_CAE_LOADED;
  const markingCfe =
    markStepMutation.isPending &&
    markStepMutation.variables === ONBOARDING_NUDGE_ACCT_CFE_HISTORY_IMPORTED;
  const markCaeDone = (): void => {
    markStepMutation.mutate(ONBOARDING_NUDGE_ACCT_CAE_LOADED);
  };
  const markCfeDone = (): void => {
    markStepMutation.mutate(ONBOARDING_NUDGE_ACCT_CFE_HISTORY_IMPORTED);
    // NOTE: we intentionally do NOT clear the owner-facing "Importar CFE"
    // nudge here. The accountant marking their own CFE-history import done
    // is separate from the owner's onboarding step; auto-clearing it hid a
    // step the owner never actually completed.
  };

  const { data: tenantPlans = [] } = useQuery({
    queryKey: ["tenants", "current", "plans", tenantId],
    queryFn: getTenantPlans,
    enabled: sessionMatches,
  });
  // The Literal E plan ($0, ∞ CFE) is only relevant to Literal E (IVA mínimo)
  // clients. The accountant knows the client's regime, so a Sí/No declaration
  // (persisted as company.literalE — trusted server-side for accountants, which
  // also flips literalEVerified so the plan activates) drives whether the card
  // shows. `/current/plans` returns every tier unfiltered, so this gate is the
  // portal's responsibility.
  // Seed the local Literal E choice from the tenant once (if already declared).
  useEffect(() => {
    if (literalESeededRef.current) return;
    if (tenant?.literalE != null) {
      setLiteralEChoice(tenant.literalE);
      literalESeededRef.current = true;
    }
  }, [tenant?.literalE]);
  // Reset per-client local state when the route switches tenants.
  //
  // The route has no `key`, so React Router RE-RENDERS this component rather
  // than remounting it when :tenantId changes. Without this, the one-shot
  // seeding refs below stay true from the previous client: their answers would
  // survive the switch, the activation gate would read as satisfied without
  // anyone being asked, and persist would write client A's declaration — a
  // legal attestation — against client B.
  //
  // Runs before the seeding effects, so a tenant switch clears first and then
  // re-seeds from the new tenant's own data.
  useEffect(() => {
    benefitSeededRef.current = false;
    setBenefitDeclaration({ complies: null, motivo: null });
    literalESeededRef.current = false;
    setLiteralEChoice(null);
  }, [tenantId]);

  // Seed the benefit declaration from an existing one, so re-entering the wizard
  // shows what was already signed instead of an empty form.
  const { data: existingBenefitDeclaration } = useQuery({
    queryKey: ["accountant-fiscal-benefit-declaration", tenantId],
    queryFn: () => getFiscalBenefitDeclarationForTenant(tenantId!),
    enabled: Boolean(tenantId),
    retry: false,
  });
  useEffect(() => {
    if (benefitSeededRef.current) return;
    if (existingBenefitDeclaration) {
      setBenefitDeclaration({
        complies: existingBenefitDeclaration.complies,
        motivo: existingBenefitDeclaration.motivo,
      });
      benefitSeededRef.current = true;
    }
  }, [existingBenefitDeclaration]);

  const literalEDeclared = literalEChoice === true;
  // Filtering is the portal's job (`/current/plans` returns every tier), but the
  // rule is the SERVER's — see visiblePlans.ts for why literalE is the one
  // deliberate exception, and its sibling test for the semilla case this fixes.
  const visiblePlans = selectVisiblePlans(
    tenantPlans,
    tenant?.eligiblePlanIds ?? null,
    literalEDeclared,
  );

  // Poll the latest pending checkout for this tenant. Returns the row
  // (with shortUrl baked in) when one exists, null when there's no
  // outstanding link. Polling stops once it flips to paid.
  const { data: pendingCheckout, refetch: refetchPendingCheckout } = useQuery({
    queryKey: ["accountant-pending-checkout", tenantId],
    // Per-tenant endpoint (tenant id from the URL + membership check), not the
    // active-tenant /current/ one — so an in-flight/stale session token can't
    // point the poll at the wrong tenant. Matches the accountant-home pattern.
    queryFn: () =>
      tenantId ? getPendingPlanCheckoutForTenant(tenantId) : Promise.resolve(null),
    enabled: sessionMatches,
    refetchInterval: (q) =>
      q.state.data?.status === "paid" ? false : 15_000,
  });
  const pendingCheckoutId = pendingCheckout?.checkoutId ?? null;
  const pendingCheckoutUrl = pendingCheckout?.shortUrl ?? null;

  // Preselect the first SELECTABLE plan as soon as plans load, so the grid
  // never renders with nothing highlighted (and never defaults to a hidden
  // Literal E card). Uses visiblePlans so the default is always activatable.
  useEffect(() => {
    if (planChoice == null && visiblePlans.length > 0) {
      setPlanChoice({ planId: visiblePlans[0]!.id, interval: "monthly" });
    }
  }, [planChoice, visiblePlans]);

  // Prefill the sucursal input from the tenant's current value (defaults
  // to 1). Ref-gated so clearing the field while editing doesn't re-seed
  // mid-keystroke.
  const sucursalSeededRef = useRef(false);
  useEffect(() => {
    if (sucursalSeededRef.current) return;
    if (tenant?.cdgDGISucur != null) {
      setSucursalInput(String(tenant.cdgDGISucur));
      sucursalSeededRef.current = true;
    }
  }, [tenant?.cdgDGISucur]);

  /** Loading gate moved BELOW all hooks (see the `if (!sessionMatches…)`
   * block right above the main JSX return). Returning early here used to
   * skip the useEffects further down (~line 707/725/734) on the first
   * render, then run them on the next once tenant landed — classic Rules
   * of Hooks violation ("Rendered more hooks than during the previous
   * render"). Optional-chain the derivations so they're safe on the
   * tenant-null pre-load render; the gate ensures we never reach JSX
   * without tenant. */
  const certUploaded = Boolean(tenant?.hasDgiCredentials);
  // Step-done check: real DGI confirmation email arrived (set by the
  // backend inbox processor). The accountant's "Ya creé el usuario en
  // DGI" tap only sets `dgiPortalUserAccountantClaimed`, which opens
  // a loading window — the step doesn't complete until DGI sends back
  // the activation email.
  const portalUserSaved = Boolean(tenant?.dgiPortalUserConfirmed);
  const portalUserRowExists = Boolean(tenant?.hasDgiPortalUser);
  const portalUserAccountantClaimed = Boolean(
    tenant?.dgiPortalUserAccountantClaimed,
  );
  // Awaiting DGI's email: accountant claimed creation but the inbox
  // processor hasn't seen the confirmation yet. Drives the loading
  // state in the JSX + the polling effect below.
  const portalUserAwaitingDgi =
    portalUserAccountantClaimed && !portalUserSaved;
  const progress = isRecord(tenant?.dgiAutoOnboardingProgress)
    ? tenant!.dgiAutoOnboardingProgress
    : null;
  const proveedorSoftwareDone = Boolean(progress?.proveedor_software);
  const idemisorSyncDone = Boolean(progress?.idemisor_sync);
  const failed = isRecord(progress?._failed) ? progress!._failed : null;
  // The merged "Configuración Fiscalo en DGI" step covers both
  // proveedor_software and IdEmisor sync. Done only when both stamped.
  // "Manual review requested" when proveedor_software is marked
  // manual_intervention (the legacy fallback path the accountant can
  // still choose if the automation can't reach DGI).
  const fiscaloConfigDone = proveedorSoftwareDone && idemisorSyncDone;
  const fiscaloConfigManualReview =
    !fiscaloConfigDone && failed?.proveedor_software === 'manual_intervention';
  // Stalled detection: if the spinner is up but the backend says no
  // session is fresh anymore (Python crashed without posting a
  // terminal status), unstick the UI and tell the user to retry.
  // Only fires AFTER we've actually observed the session running at
  // least once — otherwise the very first poll right after clicking
  // "Configurar ahora" (which often still returns running:false because
  // the orchestrator hasn't created the session row yet) would race the
  // start toast and surface a phantom failure.
  useEffect(() => {
    if (!savingFiscaloConfig) return;
    if (fiscaloConfigRunning === undefined) return; // first fetch not back
    if (fiscaloConfigRunning.running) return;
    if (!sawFiscaloRunningRef.current) return; // never saw it actually run
    if (fiscaloConfigDone) return;
    if (fiscaloConfigManualReview) return;
    toast.error(
      t(
        "accountantClientOnboarding.fiscaloConfig.stalledToast",
        "La automatización se detuvo. Volvé a intentar.",
      ),
    );
    setSavingFiscaloConfig(false);
  }, [
    savingFiscaloConfig,
    fiscaloConfigRunning,
    fiscaloConfigDone,
    fiscaloConfigManualReview,
    t,
  ]);
  const sucursalConfirmed = dismissedAlertIds.includes(
    ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED,
  );

  const fiscaloPanelConfirmed = dismissedAlertIds.includes(
    ONBOARDING_NUDGE_ACCT_FISCALO_CONFIG_CONFIRMED,
  );
  const fiscalProfilePanelConfirmed = dismissedAlertIds.includes(
    ONBOARDING_NUDGE_ACCT_FISCAL_PROFILE_CONFIRMED,
  );
  // Phase 3 is done either when the accountant explicitly resolved it
  // (sent the invite or chose to skip) or when the tenant already has a
  // non-accountant owner — covers the case where the real owner was
  // transferred outside this wizard.
  const transferResolvedFlag = dismissedAlertIds.includes(
    ONBOARDING_NUDGE_ACCT_TRANSFER_RESOLVED,
  );
  const hasNonAccountantOwner = tenantMembers.some(
    (m) => m.role === "owner" && m.userId !== user?.id,
  );
  const transferResolved = transferResolvedFlag || hasNonAccountantOwner;

  // The plan step is "done" when any of these is true:
  //   · the tenant has a paid plan (real backend signal),
  //   · the in-flight dLocal checkout flipped to 'paid',
  //   · the accountant deliberately activated a plan — paid OR free.
  //     tenant.planResolvedAt is set by the backend in both cases, so
  //     it covers "picked free" without needing a localStorage flag.
  // NOT a payment test, despite how it reads. isFreePlan is a PREMIUM-FEATURE
  // gate: it is true for the billable Tala (514) and Semilla (99) tiers, so a
  // paying client on either lands here as "not paid". It survives only as the
  // legacy fallback inside planPaid below — a grandfathered ombu/ceibo tenant
  // predating planResolvedAt has no other signal. Anything asking "does this
  // company already have a plan?" must use planPaid, not this.
  const tenantOnPremiumTier = !isFreePlan(tenant?.planId);
  const planResolvedOnBackend = tenant?.planResolvedAt != null;
  const planPaid =
    tenantOnPremiumTier ||
    pendingCheckout?.status === "paid" ||
    planResolvedOnBackend;
  // Once paid, drop the in-flight URL — keeping it would invite the
  // accountant to re-share a stale link.
  const showCheckoutLink = !planPaid && pendingCheckoutUrl != null;
  const selectedPlan = planChoice
    ? tenantPlans.find((p) => p.id === planChoice.planId) ?? null
    : null;
  // "Free" here means literally $0 for the chosen interval — the only plans the
  // backend lets us activate without a checkout. Must NOT use isFreePlan()
  // (a premium-feature gate that also matches the billable $513 Tala): routing
  // Tala through the free-activation path 400s ("Paid plans require checkout").
  // Reads the TENANT price, not the catalog price: for an Art. 5 (Literal E /
  // IVA mínimo) client the 80 UI credit is deducted server-side, so a billable
  // tier can legitimately cost $0 — and dLocal cannot bill below 1, so those
  // clients MUST go down the free-activation path or the checkout is rejected.
  const selectedIsFree =
    selectedPlan != null &&
    tenantPlanPrice(
      selectedPlan,
      planChoice?.interval === "annual" ? "annual" : "monthly",
    ) === 0;

  const displayName =
    tenant?.legalName?.trim() ||
    tenant?.name?.trim() ||
    tenant?.rut ||
    t("accountantClientOnboarding.unnamedClient", "Cliente sin nombre");

  const handleCertUploaded = async (): Promise<void> => {
    await refetchTenant();
  };

  // Accountant declares whether the client is Literal E (IVA mínimo). INSTANT —
  // local state only, no API call — so the toggle never freezes. Sí surfaces +
  // preselects the $0 Literal E plan; No falls back to a general tier if one was
  // selected. The declaration is persisted later, at activation.
  const chooseLiteralE = (value: boolean): void => {
    setLiteralEChoice(value);
    if (value) {
      setPlanChoice({
        planId: "literalE",
        interval: planChoice?.interval ?? "monthly",
      });
    } else if (planChoice?.planId === "literalE") {
      // From visiblePlans, not tenantPlans: falling back to the first tier in
      // the raw catalogue could preselect a card this client is not eligible
      // for (semilla, typically) — the activation would then 403 on a plan the
      // accountant never actively chose.
      const firstGeneral = visiblePlans.find((p) => p.id !== "literalE");
      setPlanChoice(
        firstGeneral
          ? {
              planId: firstGeneral.id,
              interval: planChoice?.interval ?? "monthly",
            }
          : null,
      );
    }
  };

  // Persist the accountant's Literal E declaration (company.literalE → trusted
  // literalEVerified server-side) right before committing a plan — this is what
  // lets the $0 Literal E activation clear the regime gate. No-op if unchanged.
  const persistLiteralEIfNeeded = async (): Promise<void> => {
    if (literalEChoice != null && tenant?.literalE !== literalEChoice) {
      await updateTenant({ literalE: literalEChoice });
    }
  };

  /**
   * The declaration must be answered before any plan can be activated: the
   * credit we later grant is evidenced by it, and a client activated without one
   * would receive a discount (or a reported credit) with nothing behind it.
   *
   * Note this GATES rather than defaults. An untouched form is still never
   * written as a negative declaration — "declined" and "never asked" are the two
   * states this record exists to tell apart, so the fix for an unanswered form is
   * to block activation, not to invent an answer.
   */
  const benefitDeclarationComplete =
    isFiscalBenefitDeclarationComplete(benefitDeclaration);

  /**
   * Record the benefit declaration alongside the plan choice. Still defensive
   * about a null answer even though activation is gated — the guard above is the
   * UI contract, this is the data contract.
   */
  const persistFiscalBenefitIfNeeded = async (): Promise<void> => {
    if (benefitDeclaration.complies == null) return;
    if (!tenantId) return;
    await recordFiscalBenefitDeclarationForTenant(tenantId, {
      complies: benefitDeclaration.complies,
      motivo: benefitDeclaration.motivo,
    });
  };

  /**
   * Done-state of the régimen step: a declaration already on the server.
   *
   * Read from the server rather than from `benefitDeclaration` (the form) on
   * purpose. The step must read as done when the accountant re-enters the
   * wizard, and must NOT read as done merely because the form has been filled
   * in but never saved.
   */
  const benefitDeclarationSaved = existingBenefitDeclaration?.complies != null;

  /**
   * Save the declaration on its own, independently of the plan.
   *
   * It used to be written only as a side effect of activating a plan, from
   * inside a block gated on `!planPaid` — so the moment a plan was resolved
   * (including picking the FREE one) the form disappeared and the declaration
   * could never be made at all. Three of one accountant's clients are stuck
   * exactly there. Its own step needs its own write.
   *
   * Explicit CTA, never auto-save: FiscalBenefitDeclarationService.record()
   * APPENDS a row every time (it never upserts), so saving on change would
   * write a signed attestation per keystroke.
   */
  const handleSaveBenefitDeclaration = async (): Promise<void> => {
    if (!tenantId || !benefitDeclarationComplete || savingBenefitDeclaration) {
      return;
    }
    setSavingBenefitDeclaration(true);
    try {
      await persistFiscalBenefitIfNeeded();
      // Both keys, from one list. Invalidating only the declaration left the
      // PLANS query holding the pre-declaration price, so the Literal E card
      // kept showing 514 after the very save that makes it 0 — see
      // benefitDeclarationInvalidations.
      await Promise.all(
        queryKeysInvalidatedByBenefitDeclaration(tenantId).map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
      toast.success(
        t(
          "accountantClientOnboarding.benefit.savedToast",
          "Declaración guardada.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.benefit.saveError",
          "No pudimos guardar la declaración.",
        ),
      );
    } finally {
      setSavingBenefitDeclaration(false);
    }
  };

  const handleActivateFreePlan = async (): Promise<void> => {
    if (!planChoice || generatingLink || !benefitDeclarationComplete) return;
    setGeneratingLink(true);
    try {
      await persistLiteralEIfNeeded();
      await persistFiscalBenefitIfNeeded();
      await updateTenantPlan(planChoice.planId, planChoice.interval);
      // Backend set company.planResolvedAt; refetching the tenant
      // surfaces the flag so the card flips to "Listo" without the
      // old localStorage round-trip.
      await refetchTenant();
      toast.success(
        t(
          "accountantClientOnboarding.plan.activatedFree",
          "Plan gratis activado.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.plan.activateError",
          "No pudimos activar el plan.",
        ),
      );
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleGenerateLink = async (): Promise<void> => {
    if (!planChoice || !tenantId || generatingLink || !benefitDeclarationComplete)
      return;
    setGeneratingLink(true);
    try {
      await persistLiteralEIfNeeded();
      await persistFiscalBenefitIfNeeded();
      // Per-tenant endpoint: the checkout is created for THIS client (tenant id
      // from the URL + membership check), independent of the session token's
      // activeTenantId. Prevents the link from silently landing on the wrong
      // tenant — or failing — when the tenant switch hasn't propagated.
      const res = await createPlanCheckoutForTenant(tenantId, {
        planId: planChoice.planId,
        billingInterval: planChoice.interval,
        returnContext: "shared_link",
      });
      if (!res.redirectUrl || !res.shortUrl) {
        toast.error(
          t(
            "accountantClientOnboarding.plan.noUrlError",
            "No pudimos generar el enlace. Probá de nuevo.",
          ),
        );
        return;
      }
      // The pending-checkout query is the source of truth — refetch
      // so the share-link UI mounts immediately. Backend auto-
      // cancels any prior pending rows so the query returns this new
      // row on next call.
      await refetchPendingCheckout();
      toast.success(
        t(
          "accountantClientOnboarding.plan.linkGenerated",
          "Enlace listo para compartir.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.plan.generateError",
          "No pudimos generar el enlace.",
        ),
      );
    } finally {
      setGeneratingLink(false);
    }
  };

  /**
   * Open (or adopt) the checkout when the transfer panel is expanded, so the
   * reference shown is the row the receipt will settle. Annual only, matching
   * what this wizard offers, and keyed on the plan so switching plans with the
   * panel open re-opens against the right amount.
   */
  useEffect(() => {
    if (!bankTransferOpen || !tenantId) return;
    if (!planChoice || planChoice.interval !== "annual") return;
    let cancelled = false;
    void createBankTransferIntentForTenant(tenantId, {
      planId: planChoice.planId,
      billingInterval: "annual",
      returnContext: "onboarding",
    })
      .then((res) => {
        if (!cancelled) setTransferCheckoutId(res.checkoutId);
      })
      .catch(() => {
        if (!cancelled) setTransferCheckoutId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bankTransferOpen, tenantId, planChoice]);

  const handleBankTransferSubmit = async (
    receipt: File,
    shownCheckoutId: string | null,
  ): Promise<void> => {
    if (!planChoice || !tenantId || submittingTransfer) return;
    if (!benefitDeclarationComplete) return;
    if (planChoice.interval !== "annual") return;
    setSubmittingTransfer(true);
    try {
      await persistLiteralEIfNeeded();
      await persistFiscalBenefitIfNeeded();
      // Per-tenant endpoint (tenant id from the URL), not the active-tenant
      // one — the wire + activation land on THIS client regardless of the
      // session token's activeTenantId.
      await createBankTransferCheckoutForTenant(tenantId, {
        planId: planChoice.planId,
        billingInterval: "annual",
        receipt,
        // The row the client was given a reference for.
        settleCheckoutId: shownCheckoutId ?? undefined,
      });
      // Backend set company.planResolvedAt — refetch so the card flips to
      // "Listo" just like the paid-link path does on webhook arrival.
      await refetchTenant();
      await refetchPendingCheckout();
      setBankTransferOpen(false);
      toast.success(
        t(
          "accountantClientOnboarding.plan.bankTransfer.activated",
          "¡Pago registrado! El plan quedó activo.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.plan.bankTransfer.error",
          "No pudimos registrar el comprobante. Probá de nuevo.",
        ),
      );
    } finally {
      setSubmittingTransfer(false);
    }
  };

  const handleCopyLink = async (): Promise<void> => {
    if (!pendingCheckoutUrl) return;
    try {
      await navigator.clipboard.writeText(pendingCheckoutUrl);
      toast.success(
        t("accountantClientOnboarding.plan.copied", "Enlace copiado."),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.plan.copyError",
          "No pudimos copiar el enlace.",
        ),
      );
    }
  };

  const handleDiscardLink = async (): Promise<void> => {
    const id = pendingCheckoutId;
    if (!id) {
      setPlanChoice(null);
      return;
    }
    try {
      if (tenantId) await cancelPlanCheckoutForTenant(tenantId, id);
    } catch {
      // Best-effort — even if cancellation fails the refetch below
      // surfaces the current backend state, and createPlanCheckout's
      // auto-cancel-prior-pending will catch up next time a link is
      // generated. Don't toast an error; the UX still moves forward.
    }
    await refetchPendingCheckout();
    setPlanChoice(null);
    toast.success(
      t(
        "accountantClientOnboarding.plan.discarded",
        "Enlace descartado. Podés generar otro.",
      ),
    );
  };

  const handleWhatsAppShare = (): void => {
    if (!pendingCheckoutUrl) return;
    const clientName = displayName;
    const message = t(
      "accountantClientOnboarding.plan.whatsappMessage",
      {
        name: clientName,
        url: pendingCheckoutUrl,
        defaultValue:
          "Hola — para activar la cuenta de {{name}} en Fiscalo, completá el pago de la suscripción acá: {{url}}",
      },
    );
    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  // Manual fallback for tenants where the Python automation can't reach
  // DGI (rare). Stamps the manual_intervention marker so the section
  // surfaces "En revisión" and the root team gets pinged via email.
  const handleRequestManualReview = async (): Promise<void> => {
    if (startingAutoFlow) return;
    setStartingAutoFlow(true);
    try {
      await requestProveedorSoftwareRegistration();
      toast.success(
        t(
          "accountantClientOnboarding.autoFlow.startedToast",
          "Solicitud enviada. Te avisamos cuando esté listo.",
        ),
      );
      await refetchTenant();
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.autoFlow.startError",
          "No pudimos enviar la solicitud.",
        ),
      );
    } finally {
      setStartingAutoFlow(false);
    }
  };

  // Merged "Configuración Fiscalo en DGI" button: backend triggers
  // whichever sub-step is pending (idemisor_sync, then auto-chained
  // proveedor_software). The accountant's UI stays in the
  // "configurando" state — spinner button, polling — until
  // dgiAutoOnboardingProgress flips both stamps, a failure marker
  // appears, or we hit the safety timeout. Completion handling lives
  // in the useEffect below this so the toast fires once on the actual
  // transition rather than every poll tick.
  const handleStartFiscaloConfig = async (): Promise<void> => {
    if (savingFiscaloConfig) return;
    setSavingFiscaloConfig(true);
    try {
      const result = await startFiscaloConfig();
      if (!result.accepted) {
        toast.error(
          result.message ||
            t(
              "accountantClientOnboarding.fiscaloConfig.startError",
              "No pudimos iniciar la configuración.",
            ),
        );
        setSavingFiscaloConfig(false);
        return;
      }
      toast.success(
        t(
          "accountantClientOnboarding.fiscaloConfig.startedToast",
          "Configurando en DGI… Tardará 1-3 minutos.",
        ),
      );
      await refetchTenant();
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfig.startError",
          "No pudimos iniciar la configuración.",
        ),
      );
      setSavingFiscaloConfig(false);
    }
  };

  // Drive the merged-button loading state off the actual backend
  // progress. Once savingFiscaloConfig flips on, this watcher waits for
  // one of: success (both stamps), manual-review escalation, a hard
  // failure marker, or a 5-minute safety timeout. Fires the matching
  // toast and clears the loading state. Re-arms each click via the
  // savingFiscaloConfig dep.
  useEffect(() => {
    if (!savingFiscaloConfig) return;

    const hardFailure = (() => {
      if (!failed) return null;
      const idem = failed.idemisor_sync;
      const prov = failed.proveedor_software;
      if (typeof idem === 'string' && idem.length > 0 && idem !== 'manual_intervention') {
        return idem;
      }
      if (typeof prov === 'string' && prov.length > 0 && prov !== 'manual_intervention') {
        return prov;
      }
      return null;
    })();

    if (fiscaloConfigDone) {
      toast.success(
        t(
          "accountantClientOnboarding.fiscaloConfig.completedToast",
          "Configuración completada en DGI.",
        ),
      );
      setSavingFiscaloConfig(false);
      return;
    }
    if (fiscaloConfigManualReview) {
      toast(
        t(
          "accountantClientOnboarding.fiscaloConfig.manualReviewToast",
          "No pudimos completarlo automáticamente. Nuestro equipo lo va a revisar.",
        ),
      );
      setSavingFiscaloConfig(false);
      return;
    }
    if (hardFailure) {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfig.failedToast",
          "La automatización falló. Reintentá o solicitá revisión manual.",
        ),
      );
      setSavingFiscaloConfig(false);
      return;
    }

    const timeout = setTimeout(() => {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfig.timeoutToast",
          "Está tardando más de lo esperado. Revisá el estado en unos minutos.",
        ),
      );
      setSavingFiscaloConfig(false);
    }, 5 * 60 * 1000);

    return () => clearTimeout(timeout);
  }, [
    savingFiscaloConfig,
    fiscaloConfigDone,
    fiscaloConfigManualReview,
    failed,
    t,
  ]);

  const handleGeneratePortalUser = async (): Promise<void> => {
    if (savingPortalUser) return;
    setSavingPortalUser(true);
    try {
      const result = await createDgiPortalUser();
      setGeneratedPortalUserPassword(result.password);
      toast.success(
        t(
          "accountantClientOnboarding.portalUser.generatedToast",
          "Usuario generado. Copiá la contraseña antes de cerrar.",
        ),
      );
      await refetchTenant();
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.portalUser.generateError",
          "No pudimos generar el usuario.",
        ),
      );
    } finally {
      setSavingPortalUser(false);
    }
  };

  // Auto-fire password generation ONLY the first time — when no DGI portal
  // user row exists yet for this tenant. Generating mints a fresh username +
  // password, so firing it on every entry would re-create the e-Factura user
  // each visit and invalidate credentials the accountant may have already
  // entered into DGI. Once a row exists we never auto-regenerate: the returning
  // accountant gets explicit "Ya creé el usuario en DGI" / "Regenerar
  // contraseña" actions instead (the plaintext is encrypted at rest, so we
  // can't re-display it, but recreating is worse than asking).
  useEffect(() => {
    if (!tenant) return;
    if (portalUserSaved) return;
    if (portalUserAccountantClaimed) return;
    if (portalUserRowExists) return;
    if (generateAutoFiredRef.current) return;
    if (generatedPortalUserPassword) return;
    if (savingPortalUser) return;
    generateAutoFiredRef.current = true;
    void handleGeneratePortalUser();
    // handleGeneratePortalUser identity changes every render — the ref
    // above guarantees a single fire per page session, so the missing
    // dep is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenant,
    portalUserSaved,
    portalUserAccountantClaimed,
    portalUserRowExists,
    generatedPortalUserPassword,
    savingPortalUser,
  ]);

  // Poll the tenant while we're waiting for DGI's confirmation email
  // (set by the inbox processor server-side). 20-second cadence
  // matches the rough latency of DGI's outbound mail relay. The
  // effect tears down as soon as `portalUserAwaitingDgi` flips false —
  // either confirmation arrived, or the accountant regenerated and
  // dropped the claim.
  useEffect(() => {
    if (!portalUserAwaitingDgi) return;
    const tick = (): void => {
      void refetchTenant();
    };
    const id = window.setInterval(tick, 20_000);
    return () => window.clearInterval(id);
  }, [portalUserAwaitingDgi, refetchTenant]);

  // Surface a success toast only when polling observes the live
  // transition from "awaiting DGI" → "confirmed" within this mount.
  // A useRef-based "already toasted" guard re-fires on every remount,
  // so a previously-confirmed tenant would re-toast on every navigation
  // back to the onboarding page. Tracking the *previous* awaiting state
  // means we only toast when the confirmation just arrived — landing
  // on the page with confirmation already true is silent.
  const wasAwaitingDgiRef = useRef(false);
  useEffect(() => {
    if (!tenant?.id) {
      wasAwaitingDgiRef.current = false;
      return;
    }
    const wasAwaiting = wasAwaitingDgiRef.current;
    wasAwaitingDgiRef.current = portalUserAwaitingDgi;
    if (!wasAwaiting) return;
    if (!portalUserSaved) return;
    toast.success(
      t(
        "accountantClientOnboarding.portalUser.confirmedToast",
        "DGI confirmó el usuario e-Factura. Paso completado.",
      ),
    );
    setGeneratedPortalUserPassword(null);
  }, [tenant?.id, portalUserSaved, portalUserAwaitingDgi, t]);

  const handleCopyDgiNotificationsEmail = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(DGI_NOTIFICATIONS_EMAIL);
      toast.success(
        t(
          "accountantClientOnboarding.portalUser.emailCopied",
          "Email copiado.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.portalUser.emailCopyError",
          "No pudimos copiar el email.",
        ),
      );
    }
  };

  const handleConfirmPortalUser = async (): Promise<void> => {
    if (confirmingPortalUser) return;
    setConfirmingPortalUser(true);
    try {
      await confirmDgiPortalUser();
      // Don't clear the generated password panel yet — the polling
      // loading-state takes over until DGI's email arrives. The
      // password stays visible in case the accountant needs to retry
      // a DGI portal step (e.g. mistyped a field).
      toast.success(
        t(
          "accountantClientOnboarding.portalUser.claimedToast",
          "Anotado. Estamos esperando la confirmación de DGI.",
        ),
      );
      await refetchTenant();
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.portalUser.confirmError",
          "No pudimos marcar el usuario como creado. Intentá de nuevo.",
        ),
      );
    } finally {
      setConfirmingPortalUser(false);
    }
  };

  const handleCopyGeneratedPassword = async (): Promise<void> => {
    if (!generatedPortalUserPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPortalUserPassword);
      toast.success(
        t(
          "accountantClientOnboarding.portalUser.passwordCopied",
          "Contraseña copiada.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.portalUser.passwordCopyError",
          "No pudimos copiar la contraseña.",
        ),
      );
    }
  };

  const handleSaveSucursal = async (): Promise<void> => {
    if (savingSucursal) return;
    const raw = sucursalInput.trim();
    if (!/^\d+$/.test(raw)) {
      toast.error(
        t(
          "accountantClientOnboarding.sucursal.invalid",
          "Ingresá un número entero entre 1 y 99.",
        ),
      );
      return;
    }
    const value = Number(raw);
    if (!Number.isInteger(value) || value < 1 || value > 99) {
      toast.error(
        t(
          "accountantClientOnboarding.sucursal.invalid",
          "Ingresá un número entero entre 1 y 99.",
        ),
      );
      return;
    }
    setSavingSucursal(true);
    try {
      await updateTenantSucursal(value);
      await dismissDashboardAlert(ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED);
      await Promise.all([
        refetchTenant(),
        queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() }),
      ]);
      setSucursalExpanded(false);
      toast.success(
        t(
          "accountantClientOnboarding.sucursal.savedToast",
          "Sucursal guardada.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.sucursal.saveError",
          "No pudimos guardar la sucursal.",
        ),
      );
    } finally {
      setSavingSucursal(false);
    }
  };

  // For homologado tenants we hide the steps homologación already covered or
  // that don't apply to a brand-new emisor: the e-Factura user, the
  // Fiscalo-proveedor config, and the CFE import (nothing to import for an
  // emisor that has never invoiced). They drop out of both the total and the
  // done-count.
  const phase1DoneCount =
    (homologado ? 0 : portalUserSaved ? 1 : 0) +
    (certUploaded ? 1 : 0) +
    (caeMarkedDone ? 1 : 0) +
    (homologado ? 0 : cfeMarkedDone ? 1 : 0) +
    (homologado ? 0 : fiscaloConfigDone ? 1 : 0) +
    (sucursalConfirmed ? 1 : 0) +
    (benefitDeclarationSaved ? 1 : 0) +
    (planPaid ? 1 : 0);
  // NOTE: this total and phase1DoneCount above must always move together.
  // If one gains a term and the other doesn't, phase1Done can never become
  // true and the resume effect below strands every tenant on phase 1.
  // The régimen declaration counts for homologado tenants too — a brand-new
  // emisor is precisely who declares "inicio de actividades".
  const phase1Total = homologado ? 5 : 8;
  const phase1Done = phase1DoneCount === phase1Total;
  // Wizard order: DGI → Perfil fiscal → Empresa (branding) → Propiedad.
  // Phase 2 = fiscal profile (validation-gated). Phase 3 = empresa
  // branding (no validation, just a stamp). Phase 4 = ownership transfer.
  const phase2Done = fiscalProfilePanelConfirmed;
  const phase3Done = fiscaloPanelConfirmed;
  const phase4Done = transferResolved;
  const overallDoneCount =
    (phase1Done ? 1 : 0) +
    (phase2Done ? 1 : 0) +
    (phase3Done ? 1 : 0) +
    (phase4Done ? 1 : 0);
  const overallTotal = 4;

  // Land on the first incomplete phase the first time we have data so
  // a refresh resumes the wizard at the right step instead of always
  // starting at Phase 1.
  useEffect(() => {
    if (initialPhaseSetRef.current) return;
    if (dashboardData == null) return;
    initialPhaseSetRef.current = true;
    if (!phase1Done) {
      setPhase(1);
    } else if (!phase2Done) {
      setPhase(2);
    } else if (!phase3Done) {
      setPhase(3);
    } else {
      setPhase(4);
    }
  }, [dashboardData, phase1Done, phase2Done, phase3Done]);

  // Smooth-scroll to top on every phase transition. Skipped on the
  // first render so we don't override the user's scroll position
  // when they arrive deep-linked or via back-nav.
  //
  // The window itself doesn't scroll on this route — html/body/#root
  // are all overflow:hidden and OnboardingLayout's outer div carries
  // overflow-y:auto. Walk up from a known element inside the page
  // (OnboardingShell renders .fx-onb-stage) to find the first
  // scrollable ancestor, matching the pattern in OnboardingShell.tsx.
  const phaseScrollSkipRef = useRef(true);
  useEffect(() => {
    if (phaseScrollSkipRef.current) {
      phaseScrollSkipRef.current = false;
      return;
    }
    let el: HTMLElement | null =
      document.querySelector<HTMLElement>('.fx-onb-stage');
    while (el && el !== document.body) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        el.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      el = el.parentElement;
    }
  }, [phase]);

  // Seed Phase 2 form values once tenant data lands. We use refs (vs.
  // depending on the input value) so clearing the field doesn't re-fire
  // the seed and overwrite the accountant's edit mid-keystroke. After
  // the first seed the input is fully user-controlled.
  const publicNameSeededRef = useRef(false);
  useEffect(() => {
    if (publicNameSeededRef.current) return;
    if (tenant?.name) {
      setPublicNameInput(tenant.name);
      publicNameSeededRef.current = true;
    }
  }, [tenant?.name]);
  // Mirror the settings page behaviour: assume same logo for both site
  // and invoice until the tenant explicitly diverges them.
  useEffect(() => {
    if (tenant == null) return;
    const same =
      tenant.invoiceLogoImage == null ||
      tenant.invoiceLogoImage === tenant.profileImage;
    setUseSameLogo(same);
  }, [tenant?.invoiceLogoImage, tenant?.profileImage]);

  const handleSavePublicName = async (): Promise<void> => {
    if (savingPublicName) return;
    const trimmed = publicNameInput.trim();
    if (trimmed === "") {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.publicName.empty",
          "Ingresá un nombre público.",
        ),
      );
      return;
    }
    if (trimmed === (tenant?.name ?? "")) return;
    setSavingPublicName(true);
    try {
      await updateTenant({ name: trimmed });
      await refetchTenant();
      toast.success(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.publicName.savedToast",
          "Nombre público guardado.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.publicName.saveError",
          "No pudimos guardar el nombre.",
        ),
      );
    } finally {
      setSavingPublicName(false);
    }
  };

  // Matches the settings page's brand color signature so we can pass it
  // straight to the shared CompanyImageCard.
  const handleSaveBrandColor = async (
    brandColor: string | null,
  ): Promise<void> => {
    setBrandColorSaving(true);
    try {
      await updateTenantBranding(brandColor);
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.brandColor.savedToast",
          "Color de marca guardado.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.brandColor.saveError",
          "No pudimos guardar el color.",
        ),
      );
    } finally {
      setBrandColorSaving(false);
    }
  };

  const uploadLogo = async (
    file: File | null,
    target: "both" | "site" | "invoice",
    setUploading: (v: boolean) => void,
  ): Promise<void> => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadCurrentTenantImage(file, target);
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      await refreshUser();
      toast.success(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.logo.savedToast",
          "Logo guardado.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.logo.saveError",
          "No pudimos subir el logo.",
        ),
      );
    } finally {
      setUploading(false);
    }
  };
  const handleBothLogoChange = (file: File | null): void => {
    void uploadLogo(file, "both", setCompanyImageUploading);
  };
  const handleSiteLogoChange = (file: File | null): void => {
    void uploadLogo(file, "site", setSiteLogoUploading);
  };
  const handleInvoiceLogoChange = (file: File | null): void => {
    void uploadLogo(file, "invoice", setInvoiceLogoUploading);
  };

  /**
   * Crop handlers for the shared CompanyImageCard.
   *
   * Without these the card renders its "Ajustar logo" button (which only
   * needs an invoice logo to exist) but never mounts InvoiceLogoCropDialog
   * (which is gated on onSaveInvoiceLogoCrop), so the button was simply dead
   * on this page — an accountant reported clicking it and nothing happening.
   * Same current-tenant endpoints the rest of this panel already uses.
   */
  const handleSaveInvoiceLogoCrop = async (
    payload: InvoiceLogoCropPayload,
  ): Promise<void> => {
    setInvoiceLogoCropSaving(true);
    try {
      // Pass the CURRENT colour through: the endpoint owns colour and crop
      // together, so sending null would clear a colour nobody touched.
      await updateTenantBranding(tenant?.brandColor ?? null, payload);
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success(t("settings.companyImage.crop.success"));
    } catch {
      toast.error(t("settings.companyImage.crop.error"));
    } finally {
      setInvoiceLogoCropSaving(false);
    }
  };

  /**
   * Upload a newly picked logo and apply the crop drawn for it as one action.
   * Order matters: the upload RESETS the stored crop (a crop is only
   * meaningful against the image it was drawn on), so the crop is saved after.
   */
  const handleUploadInvoiceLogoWithCrop = async (
    file: File,
    target: "both" | "invoice",
    crop: InvoiceLogoCropPayload,
  ): Promise<void> => {
    const setUploading =
      target === "both" ? setCompanyImageUploading : setInvoiceLogoUploading;
    setUploading(true);
    try {
      await uploadCurrentTenantImage(file, target);
      await updateTenantBranding(tenant?.brandColor ?? null, crop);
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      await refreshUser();
      toast.success(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.logo.savedToast",
          "Logo guardado.",
        ),
      );
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.logo.saveError",
          "No pudimos subir el logo.",
        ),
      );
    } finally {
      setUploading(false);
    }
  };

  // Phase 3 (Empresa branding): just stamps the panel as confirmed. Name +
  // logo saves happen inline on their own CTAs, so there's no aggregate
  // validate step here — clicking "Confirmar y seguir" just advances to
  // phase 4 (ownership transfer).
  const handleConfirmFiscaloPanel = async (): Promise<void> => {
    if (confirmingFiscaloPanel) return;
    setConfirmingFiscaloPanel(true);
    try {
      // Stamp the accountant-side phase-confirmed alert. We deliberately do
      // NOT dismiss the owner-side brand/logo nudge here: uploading a logo is
      // the owner's own (optional) branding choice, so the accountant flow
      // shouldn't suppress that step from the owner's dashboard.
      await dismissDashboardAlert(ONBOARDING_NUDGE_ACCT_FISCALO_CONFIG_CONFIRMED);
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
      toast.success(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.confirmedToast",
          "Configuración guardada.",
        ),
      );
      setPhase(4);
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.fiscaloConfigPanel.confirmError",
          "No pudimos guardar. Probá de nuevo.",
        ),
      );
    } finally {
      setConfirmingFiscaloPanel(false);
    }
  };

  // Phase 2 (perfil fiscal): validates + persists the FiscalProfile via the
  // editor ref, then stamps the panel as confirmed. Separate from phase 3's
  // handler because branding stamps on its own without a validation gate.
  const handleConfirmFiscalProfilePanel = async (): Promise<void> => {
    if (confirmingFiscalProfilePanel) return;
    setConfirmingFiscalProfilePanel(true);
    try {
      const profileRef = fiscalProfileRef.current;
      if (profileRef && !profileRef.isValid()) {
        toast.error(
          t(
            "accountantClientOnboarding.fiscaloConfigPanel.fiscalProfile.invalid",
            "Completá el perfil fiscal antes de confirmar.",
          ),
        );
        setConfirmingFiscalProfilePanel(false);
        return;
      }
      if (profileRef) await profileRef.save();
      await dismissDashboardAlert(ONBOARDING_NUDGE_ACCT_FISCAL_PROFILE_CONFIRMED);
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
      toast.success(
        t(
          "accountantClientOnboarding.fiscalProfilePanel.confirmedToast",
          "Perfil fiscal guardado.",
        ),
      );
      setPhase(3);
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.fiscalProfilePanel.confirmError",
          "No pudimos guardar. Probá de nuevo.",
        ),
      );
    } finally {
      setConfirmingFiscalProfilePanel(false);
    }
  };

  const handleResolveTransfer = async (
    mode: "invited" | "skipped",
  ): Promise<void> => {
    if (resolvingTransfer) return;
    setResolvingTransfer(true);
    try {
      await dismissDashboardAlert(ONBOARDING_NUDGE_ACCT_TRANSFER_RESOLVED);
      // Phase 4 is the last step of the accountant wizard — flip the
      // tenant's onboardingCompletedAt so the "Onboarding pendiente"
      // pill clears in the clients list. Best-effort: if a backend
      // prereq is missing (cert, DGI portal user, etc.) we don't want
      // to block the UX, the accountant can finalise it later.
      try {
        await completeOnboarding();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("completeOnboarding failed at end of acct wizard", err);
      }
      // Mark only the "Invitá a tu contador" step as handled — the accountant
      // IS the contador. We deliberately do NOT dismiss the owner-facing
      // CFE-import or brand/logo nudges: those are the owner's own steps, and
      // auto-clearing them hid steps the owner never actually completed.
      await Promise.allSettled([
        dismissDashboardAlert(ONBOARDING_NUDGE_ACCOUNTANT_INVITE),
      ]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() }),
        queryClient.invalidateQueries({ queryKey: tenantKeys.current() }),
        queryClient.invalidateQueries({ queryKey: ["accountant"] }),
        refetchMembers(),
      ]);
      toast.success(
        mode === "invited"
          ? t(
              "accountantClientOnboarding.transfer.invitedToast",
              "Invitación enviada. Cuando la persona acepte queda como propietario.",
            )
          : t(
              "accountantClientOnboarding.transfer.skippedToast",
              "Listo — quedás como propietario de esta empresa.",
            ),
      );
      navigate("/accountant", { replace: true });
    } catch {
      toast.error(
        t(
          "accountantClientOnboarding.transfer.resolveError",
          "No pudimos guardar el cambio.",
        ),
      );
    } finally {
      setResolvingTransfer(false);
    }
  };

  /** Loading gate — placed AFTER all hooks. See the rationale comment near
   * the top of the function where the early return used to live.
   *
   * The third check (tenant.id !== tenantId) catches a race that surfaced
   * in the field: switchTenant awaits applySelectTenantResponse (which
   * itself awaits getSession), then removeQueries. Between the user-state
   * update and the cache wipe React can render once with
   * sessionMatches=true but the previous tenant's data still cached under
   * tenantKeys.current() — the user sees the prior company's name and
   * onboarding progress for one frame. Holding the loading screen until
   * tenant.id matches the URL guarantees we never paint mismatched data. */
  if (!sessionMatches || !tenant || tenant.id !== tenantId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  // Nuevo-emisor branch. When the accountant flagged this client as a
  // brand-new electronic emisor ("scratch"), replace the standard DGI
  // phase set with the homologación-simplificada state machine. The
  // already_issuer/existing path below is left 100% unchanged. The session
  // is already switched to this tenant (loading gate above), so the
  // ingreso-simplificado endpoints resolve the right company via
  // activeTenantId.
  // Render the homologación wizard until the accountant continues past its
  // "completa" panel (see `skipHomologacionWizard`). Then we fall through to
  // the standard wizard (with the e-Factura-user + Fiscalo-proveedor steps
  // hidden — see `homologado`) so they can finish certificate / CAE / plan.
  if (
    tenant.onboardingSituationChoice === "scratch" &&
    !skipHomologacionWizard
  ) {
    return (
      <OnboardingShell
        variant="single"
        form={
          <>
            <OnboardingBrandMark />
            <AccountantHomologacionSimplificadaFlow
              displayName={displayName}
              portalUserRowExists={portalUserRowExists}
              onExit={() => navigate("/accountant", { replace: true })}
              onContinue={handleContinuePastHomologacion}
            />
          </>
        }
      />
    );
  }

  return (
    <OnboardingShell
      variant="single"
      form={
        <>
          <OnboardingBrandMark />
          <div className="fx-acct-setup-meta">
            <span className="fx-acct-setup-eyebrow">{displayName}</span>
            <span className="fx-acct-setup-progress">
              {t(
                "accountantClientOnboarding.overallProgress",
                "Fase {{current}} de {{total}}",
                { current: phase, total: overallTotal },
              )}
              {" · "}
              {overallDoneCount}/{overallTotal}{" "}
              {t("accountantClientOnboarding.done", "listas")}
            </span>
          </div>

          <PhaseStepper
            current={phase}
            phase1Done={phase1Done}
            phase2Done={phase2Done}
            phase3Done={phase3Done}
            phase4Done={phase4Done}
            onJump={setPhase}
            labels={{
              phase1: t(
                "accountantClientOnboarding.phaseStepper.phase1",
                "DGI",
              ),
              phase2: t(
                "accountantClientOnboarding.phaseStepper.phase2",
                "Perfil fiscal",
              ),
              phase3: t(
                "accountantClientOnboarding.phaseStepper.phase3",
                "Empresa",
              ),
              phase4: t(
                "accountantClientOnboarding.phaseStepper.phase4",
                "Propiedad",
              ),
            }}
          />

          <h1 className="fx-onb-h1">
            {phase === 1 ? (
              phase1Done ? (
                <>
                  {t("accountantClientOnboarding.titleDoneLead", "Todo")}{" "}
                  <span className="ital">
                    {t(
                      "accountantClientOnboarding.titleDoneAccent",
                      "listo.",
                    )}
                  </span>
                </>
              ) : (
                <>
                  {t("accountantClientOnboarding.titleLead", "Configurá")}{" "}
                  <span className="ital">
                    {t("accountantClientOnboarding.titleAccent", "DGI.")}
                  </span>
                </>
              )
            ) : phase === 2 ? (
              <>
                {t(
                  "accountantClientOnboarding.phase2.titleLead",
                  "Perfil",
                )}{" "}
                <span className="ital">
                  {t(
                    "accountantClientOnboarding.phase2.titleAccent",
                    "fiscal.",
                  )}
                </span>
              </>
            ) : phase === 3 ? (
              <>
                {t(
                  "accountantClientOnboarding.phase3.titleLead",
                  "Identidad de la",
                )}{" "}
                <span className="ital">
                  {t(
                    "accountantClientOnboarding.phase3.titleAccent",
                    "empresa.",
                  )}
                </span>
              </>
            ) : (
              <>
                {t(
                  "accountantClientOnboarding.phase4.titleLead",
                  "Transferí la",
                )}{" "}
                <span className="ital">
                  {t(
                    "accountantClientOnboarding.phase4.titleAccent",
                    "propiedad.",
                  )}
                </span>
              </>
            )}
          </h1>
          <p className="fx-onb-lead">
            {phase === 1
              ? phase1Done
                ? t(
                    "accountantClientOnboarding.leadDone",
                    "Configuración con DGI lista. Pasamos a configurar Fiscalo para el cliente.",
                  )
                : t(
                    "accountantClientOnboarding.lead",
                    "Pasos para dejar la cuenta del cliente lista frente a DGI: credenciales, certificado, CAEs, histórico y configuración.",
                  )
              : phase === 2
                ? t(
                    "accountantClientOnboarding.phase2.lead",
                    "Tipo de empresa, régimen de IVA / IRAE y obligaciones. Esto alimenta el cálculo automático de cada vencimiento.",
                  )
                : phase === 3
                  ? t(
                      "accountantClientOnboarding.phase3.lead",
                      "Datos para que las facturas y el portal salgan con la identidad del cliente: nombre, logo y color.",
                    )
                  : t(
                      "accountantClientOnboarding.phase4.lead",
                      "Invitá al titular real del cliente. Cuando acepte, queda como propietario y vos pasás a tener rol de contador.",
                    )}
          </p>

          {phase === 1 ? (
          <>
          {homologado && !emisorVigente ? (
            <div
              className="fx-acct-vigencia-note"
              role="status"
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                padding: "12px 14px",
                marginBottom: 16,
                borderRadius: 8,
                border: "1px solid #FBBF24",
                background: "#FFFBEB",
                color: "#92400E",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              <Clock aria-hidden style={{ flexShrink: 0, marginTop: 1 }} size={16} />
              <span>
                {fechaVigencia
                  ? t(
                      "accountantClientOnboarding.vigencia.dated",
                      "El emisor va a poder facturar a partir del {{fecha}} (Fecha de Vigencia de DGI). Podés dejar todo configurado ahora; te avisamos por email cuando quede habilitado.",
                      { fecha: formatDisplayDate(fechaVigencia) },
                    )
                  : t(
                      "accountantClientOnboarding.vigencia.pending",
                      "DGI habilita al emisor para facturar el día hábil siguiente a la resolución. Podés dejar todo configurado ahora; te avisamos por email cuando quede habilitado.",
                    )}
              </span>
            </div>
          ) : null}
          {!homologado ? (
          <div
            className={cn("fx-acct-section", portalUserSaved && "is-done")}
          >
            <div className="fx-acct-section-head">
              <div className="fx-acct-section-ico">
                {portalUserSaved ? <Check aria-hidden /> : <User aria-hidden />}
              </div>
              <div className="fx-acct-section-text">
                <div className="title">
                  {t(
                    "accountantClientOnboarding.portalUser.title",
                    "Usuario e-Factura DGI",
                  )}
                </div>
                <div className="meta">
                  {portalUserSaved
                    ? t(
                        "accountantClientOnboarding.portalUser.done",
                        "Credenciales guardadas.",
                      )
                    : t(
                        "accountantClientOnboarding.portalUser.meta",
                        "Necesitamos las credenciales del usuario e-Factura del cliente en DGI para que Fiscalo pueda configurarse automáticamente.",
                      )}
                </div>
              </div>
              {portalUserSaved ? (
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
              ) : null}
            </div>
            {/* Render the body when either the step isn't saved yet OR
                we just generated a password (so the accountant can copy
                it). Previously gated only on `!portalUserSaved`, which
                made the password panel disappear instantly after the
                backend save — race that hid the only chance to read
                it. The body now persists until the accountant taps
                "Listo, ya guardé la contraseña" inside the password
                card, which clears generatedPortalUserPassword and lets
                the section collapse to its done state. */}
            {(!portalUserSaved || generatedPortalUserPassword) ? (
              <div className="fx-acct-section-body">
                <div className="fx-acct-form">
                    <p className="fx-acct-form-help">
                      {generatedPortalUserPassword
                        ? t(
                            "accountantClientOnboarding.portalUser.nextStepHint",
                            "Entrá al portal de DGI, creá el usuario e-Factura del cliente y completá el formulario con estos datos:",
                          )
                        : portalUserRowExists
                          ? t(
                              "accountantClientOnboarding.portalUser.alreadyGeneratedHint",
                              "Ya generaste las credenciales del usuario e-Factura para este cliente. Si ya lo creaste en DGI, confirmalo abajo. Si perdiste la contraseña, podés regenerarla.",
                            )
                          : t(
                              "accountantClientOnboarding.portalUser.generateHint",
                              "Estos son los datos del formulario en DGI. Generá la contraseña al final y después creá el usuario en el portal.",
                            )}
                    </p>
                    <div className="fx-acct-generated-password">
                      <div className="fx-acct-generated-password-label">
                        {t(
                          "accountantClientOnboarding.portalUser.cedulaLabel",
                          "Cédula (tuya, como contador)",
                        )}
                      </div>
                      <div className="fx-acct-generated-password-value">
                        {user?.documentNumber ??
                          t(
                            "accountantClientOnboarding.portalUser.cedulaMissing",
                            "Completá tu cédula en tu perfil",
                          )}
                      </div>
                    </div>
                    {generatedPortalUserPassword ? (
                      <div className="fx-acct-generated-password">
                        <div className="fx-acct-generated-password-label">
                          {t(
                            "accountantClientOnboarding.portalUser.passwordLabel",
                            "Contraseña generada",
                          )}
                        </div>
                        <div className="fx-acct-generated-password-value">
                          {generatedPortalUserPassword}
                        </div>
                        <div className="fx-acct-section-btn-group">
                          <button
                            type="button"
                            className="fx-acct-section-btn fx-acct-section-btn-secondary"
                            onClick={() => void handleCopyGeneratedPassword()}
                          >
                            <Copy aria-hidden />
                            {t(
                              "accountantClientOnboarding.portalUser.copyPassword",
                              "Copiar contraseña",
                            )}
                          </button>
                          <button
                            type="button"
                            className="fx-acct-section-btn fx-acct-section-btn-secondary"
                            onClick={() => void handleGeneratePortalUser()}
                            disabled={savingPortalUser || confirmingPortalUser}
                          >
                            {savingPortalUser ? (
                              <Loader2 className="animate-spin" aria-hidden />
                            ) : (
                              <RefreshCw aria-hidden />
                            )}
                            {t(
                              "accountantClientOnboarding.portalUser.regenerate",
                              "Regenerar",
                            )}
                          </button>
                        </div>
                      </div>
                    ) : savingPortalUser ? (
                      <div className="fx-acct-generated-password">
                        <div className="fx-acct-generated-password-label">
                          {t(
                            "accountantClientOnboarding.portalUser.passwordLabel",
                            "Contraseña generada",
                          )}
                        </div>
                        <div
                          className="fx-acct-generated-password-value"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            color: "var(--fx-ink-500)",
                          }}
                        >
                          <Loader2 className="animate-spin" aria-hidden />
                          {t(
                            "accountantClientOnboarding.portalUser.generating",
                            "Generando…",
                          )}
                        </div>
                      </div>
                    ) : null}
                    <div className="fx-acct-generated-password">
                      <div className="fx-acct-generated-password-label">
                        {t(
                          "accountantClientOnboarding.portalUser.emailLabel",
                          "Email de notificaciones",
                        )}
                      </div>
                      <div className="fx-acct-generated-password-value">
                        {DGI_NOTIFICATIONS_EMAIL}
                      </div>
                      <button
                        type="button"
                        className="fx-acct-section-btn fx-acct-section-btn-secondary"
                        onClick={() => void handleCopyDgiNotificationsEmail()}
                      >
                        <Copy aria-hidden />
                        {t(
                          "accountantClientOnboarding.portalUser.copyEmail",
                          "Copiar email",
                        )}
                      </button>
                    </div>
                    <div className="fx-onb-roles">
                      <div className="role">
                        <span className="check" aria-hidden>
                          <Check />
                        </span>
                        <strong>
                          {t(
                            "accountantClientOnboarding.portalUser.role1",
                            "Homologación simplificada",
                          )}
                        </strong>
                      </div>
                      <div className="role">
                        <span className="check" aria-hidden>
                          <Check />
                        </span>
                        <strong>
                          {t(
                            "accountantClientOnboarding.portalUser.role2",
                            "Producción",
                          )}
                        </strong>
                      </div>
                      <span className="note">
                        {t(
                          "accountantClientOnboarding.portalUser.rolesNote",
                          "Marcá ambos roles en la misma pantalla antes de enviar el formulario.",
                        )}
                      </span>
                    </div>
                    {portalUserAwaitingDgi ? (
                      <div className="fx-acct-form-help" role="status">
                        <Loader2
                          className="animate-spin"
                          aria-hidden
                          style={{
                            width: 14,
                            height: 14,
                            verticalAlign: "-2px",
                            marginRight: 6,
                            color: "var(--fx-ink-500)",
                          }}
                        />
                        {t(
                          "accountantClientOnboarding.portalUser.awaitingDgi",
                          "Esperando la confirmación de DGI. Suele tardar unos minutos. Cuando llegue el correo automático damos por completado este paso — te avisamos.",
                        )}
                      </div>
                    ) : null}
                    {portalUserAwaitingDgi ? (
                      // Awaiting DGI's email — no primary CTA. The
                      // help text above carries the state; Regenerar
                      // sits as an escape hatch in case the email
                      // never arrives.
                      <div className="fx-acct-section-cta">
                        <button
                          type="button"
                          className="fx-acct-section-mark"
                          onClick={() => void handleGeneratePortalUser()}
                          disabled={savingPortalUser}
                        >
                          {savingPortalUser ? (
                            <Loader2 className="animate-spin" aria-hidden />
                          ) : null}
                          {t(
                            "accountantClientOnboarding.portalUser.regenerate",
                            "Regenerar contraseña",
                          )}
                        </button>
                      </div>
                    ) : generatedPortalUserPassword ? (
                      // Password on screen → only primary CTA is
                      // "Ya creé el usuario en DGI". Regenerate now
                      // lives inside the password card next to Copy.
                      <div className="fx-acct-section-cta">
                        <button
                          type="button"
                          className="fx-acct-section-btn"
                          onClick={() => void handleConfirmPortalUser()}
                          disabled={confirmingPortalUser}
                        >
                          {confirmingPortalUser ? (
                            <Loader2 className="animate-spin" aria-hidden />
                          ) : (
                            <Check aria-hidden />
                          )}
                          {t(
                            "accountantClientOnboarding.portalUser.confirmCreated",
                            "Ya creé el usuario en DGI",
                          )}
                        </button>
                      </div>
                    ) : portalUserRowExists ? (
                      // A portal-user row already exists from a previous
                      // session, but we don't hold the plaintext (encrypted
                      // at rest). We must NOT auto-regenerate — that would
                      // mint new credentials and invalidate what the
                      // accountant may have already entered in DGI. Offer an
                      // explicit claim + a regenerate escape hatch.
                      <div className="fx-acct-section-cta">
                        <button
                          type="button"
                          className="fx-acct-section-btn"
                          onClick={() => void handleConfirmPortalUser()}
                          disabled={confirmingPortalUser || savingPortalUser}
                        >
                          {confirmingPortalUser ? (
                            <Loader2 className="animate-spin" aria-hidden />
                          ) : (
                            <Check aria-hidden />
                          )}
                          {t(
                            "accountantClientOnboarding.portalUser.confirmCreated",
                            "Ya creé el usuario en DGI",
                          )}
                        </button>
                        <button
                          type="button"
                          className="fx-acct-section-btn fx-acct-section-btn-secondary"
                          onClick={() => void handleGeneratePortalUser()}
                          disabled={savingPortalUser || confirmingPortalUser}
                        >
                          {savingPortalUser ? (
                            <Loader2 className="animate-spin" aria-hidden />
                          ) : (
                            <RefreshCw aria-hidden />
                          )}
                          {t(
                            "accountantClientOnboarding.portalUser.regenerate",
                            "Regenerar contraseña",
                          )}
                        </button>
                      </div>
                    ) : !savingPortalUser ? (
                      // No row yet and the auto-fire effect failed — give an
                      // explicit generate button so the user isn't stranded.
                      <div className="fx-acct-section-cta">
                        <button
                          type="button"
                          className="fx-acct-section-btn"
                          onClick={() => void handleGeneratePortalUser()}
                        >
                          {t(
                            "accountantClientOnboarding.portalUser.generate",
                            "Generar contraseña",
                          )}
                          <ArrowRight aria-hidden />
                        </button>
                      </div>
                    ) : null}
                </div>
              </div>
            ) : null}
          </div>
          ) : null}

          <div
            className={cn(
              "fx-acct-section",
              certUploaded && "is-done",
            )}
          >
            <div className="fx-acct-section-head">
              <div className="fx-acct-section-ico">
                {certUploaded ? (
                  <Check aria-hidden />
                ) : (
                  <ShieldCheck aria-hidden />
                )}
              </div>
              <div className="fx-acct-section-text">
                <div className="title">
                  {t(
                    "accountantClientOnboarding.cert.title",
                    "Certificado Digital",
                  )}
                </div>
                <div className="meta">
                  {certUploaded
                    ? t(
                        "accountantClientOnboarding.cert.done",
                        "Cargado y vigente",
                      )
                    : t(
                        "accountantClientOnboarding.cert.pending",
                        "Archivo .pfx + frase secreta",
                      )}
                </div>
              </div>
              {certUploaded ? (
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
              ) : null}
            </div>
            {!certUploaded ? (
              <div className="fx-acct-section-body">
                <CertificateUploadForm
                  tenantRut={tenant.rut ?? null}
                  onSuccess={handleCertUploaded}
                  idPrefix="acct-cert"
                />
              </div>
            ) : null}
          </div>

          <div
            className={cn("fx-acct-section", caeMarkedDone && "is-done")}
          >
            <div className="fx-acct-section-head">
              <div className="fx-acct-section-ico">
                {caeMarkedDone ? (
                  <Check aria-hidden />
                ) : (
                  <FileText aria-hidden />
                )}
              </div>
              <div className="fx-acct-section-text">
                <div className="title">
                  {t("accountantClientOnboarding.cae.title", "CAEs")}
                </div>
                <div className="meta">
                  {t(
                    "accountantClientOnboarding.cae.meta",
                    "Subí los CAEs activos de la empresa para que Fiscalo pueda numerar comprobantes.",
                  )}
                </div>
              </div>
              {caeMarkedDone ? (
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
              ) : null}
            </div>
            {!caeMarkedDone ? (
              <div className="fx-acct-section-body fx-acct-section-cta">
                <button
                  type="button"
                  className="fx-acct-section-mark"
                  onClick={markCaeDone}
                  disabled={markingCae}
                >
                  {markingCae ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : null}
                  {t(
                    "accountantClientOnboarding.markDone",
                    "Marcar como hecho",
                  )}
                </button>
                <button
                  type="button"
                  className="fx-acct-section-btn"
                  onClick={() => setCaeDialogOpen(true)}
                >
                  {t("accountantClientOnboarding.cae.upload", "Subir CAEs")}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            ) : null}
          </div>

          {!homologado ? (
          <div
            className={cn("fx-acct-section", cfeMarkedDone && "is-done")}
          >
            <div className="fx-acct-section-head">
              <div className="fx-acct-section-ico">
                {cfeMarkedDone ? (
                  <Check aria-hidden />
                ) : (
                  <Receipt aria-hidden />
                )}
              </div>
              <div className="fx-acct-section-text">
                <div className="title">
                  {t(
                    "accountantClientOnboarding.cfe.title",
                    "Histórico de CFEs",
                  )}
                </div>
                <div className="meta">
                  {t(
                    "accountantClientOnboarding.cfe.meta",
                    "Importá los CFEs emitidos y recibidos previos para que estado fiscal e IVA reflejen la realidad desde el día uno.",
                  )}
                </div>
              </div>
              {cfeMarkedDone ? (
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
              ) : null}
            </div>
            {!cfeMarkedDone ? (
              <div className="fx-acct-section-body fx-acct-section-cta">
                <button
                  type="button"
                  className="fx-acct-section-mark"
                  onClick={markCfeDone}
                  disabled={markingCfe}
                >
                  {markingCfe ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : null}
                  {t(
                    "accountantClientOnboarding.markDone",
                    "Marcar como hecho",
                  )}
                </button>
                <div className="fx-acct-section-btn-group">
                  <button
                    type="button"
                    className="fx-acct-section-btn fx-acct-section-btn-secondary"
                    onClick={() => setCfeRecibidasDialogOpen(true)}
                  >
                    {t(
                      "accountantClientOnboarding.cfe.uploadRecibidos",
                      "Subir recibidos",
                    )}
                  </button>
                  <button
                    type="button"
                    className="fx-acct-section-btn"
                    onClick={() => setCfeEmittedDialogOpen(true)}
                  >
                    {t(
                      "accountantClientOnboarding.cfe.uploadEmitted",
                      "Subir emitidos",
                    )}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          ) : null}

          <div
            className={cn(
              "fx-acct-section",
              sucursalConfirmed && "is-done",
            )}
          >
            {sucursalConfirmed ? (
              // Done: collapsed summary. The whole head is a button so the
              // accountant can re-open the editor to review or change the code.
              <button
                type="button"
                className="fx-acct-section-head fx-acct-section-head-toggle"
                onClick={() => setSucursalExpanded((v) => !v)}
                aria-expanded={sucursalExpanded}
              >
                <div className="fx-acct-section-ico">
                  <Check aria-hidden />
                </div>
                <div className="fx-acct-section-text">
                  <div className="title">
                    {t(
                      "accountantClientOnboarding.sucursal.title",
                      "Sucursal activa",
                    )}
                  </div>
                  <div className="meta">
                    {t(
                      "accountantClientOnboarding.sucursal.doneDetail",
                      "Código de sucursal (CdgDGISucur): {{value}}. Lo dejamos en el valor por defecto de DGI —casi todas las empresas usan 1—. Tocá la tarjeta para revisarlo o cambiarlo.",
                      { value: tenant.cdgDGISucur ?? 1 },
                    )}
                  </div>
                </div>
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
                <ChevronDown
                  className={cn(
                    "fx-acct-section-chevron",
                    sucursalExpanded && "is-open",
                  )}
                  aria-hidden
                />
              </button>
            ) : (
              <div className="fx-acct-section-head">
                <div className="fx-acct-section-ico">
                  <MapPin aria-hidden />
                </div>
                <div className="fx-acct-section-text">
                  <div className="title">
                    {t(
                      "accountantClientOnboarding.sucursal.title",
                      "Sucursal activa",
                    )}
                  </div>
                  <div className="meta">
                    {t(
                      "accountantClientOnboarding.sucursal.meta",
                      "Confirmá el código de sucursal (CdgDGISucur) que DGI asignó al cliente. Casi todas las empresas usan 1; migraciones desde otros proveedores pueden tener otro.",
                    )}
                  </div>
                </div>
              </div>
            )}
            {!sucursalConfirmed || sucursalExpanded ? (
              <div className="fx-acct-section-body">
                <label className="fx-acct-form-field">
                  <span className="fx-acct-form-label">
                    {t(
                      "accountantClientOnboarding.sucursal.label",
                      "Código de sucursal",
                    )}
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="fx-acct-form-input"
                    value={sucursalInput}
                    onChange={(e) =>
                      setSucursalInput(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    placeholder="1"
                    maxLength={2}
                  />
                </label>
                <div className="fx-acct-section-cta">
                  <button
                    type="button"
                    className="fx-acct-section-btn"
                    onClick={() => void handleSaveSucursal()}
                    disabled={savingSucursal || sucursalInput === ""}
                  >
                    {savingSucursal ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {sucursalConfirmed
                      ? t(
                          "accountantClientOnboarding.sucursal.saveChanges",
                          "Guardar cambios",
                        )
                      : t(
                          "accountantClientOnboarding.sucursal.save",
                          "Confirmar sucursal",
                        )}
                    <ArrowRight aria-hidden />
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {/* Régimen / beneficio fiscal — its own step, deliberately placed
              BEFORE the plan section: the declared motivo is what decides
              whether this client is offered discounted pricing, so it has to
              be answered before a plan is picked. It used to live inside the
              plan block, which renders only while !planPaid — so resolving a
              plan (free ones included) hid it forever and the declaration
              could never be made. Its own section is reachable whatever the
              plan state, and its own checklist row makes a missing
              declaration visible instead of silently absent. */}
          <div
            className={cn(
              "fx-acct-section",
              benefitDeclarationSaved && "is-done",
            )}
          >
            <div className="fx-acct-section-head">
              <div className="fx-acct-section-ico">
                {benefitDeclarationSaved ? (
                  <Check aria-hidden />
                ) : (
                  <BadgePercent aria-hidden />
                )}
              </div>
              <div className="fx-acct-section-text">
                <div className="title">
                  {t(
                    "accountantClientOnboarding.benefit.title",
                    "Beneficio fiscal DGI (Decreto 206/019)",
                  )}
                </div>
                <div className="meta">
                  {t(
                    "accountantClientOnboarding.benefit.meta",
                    "Declarás si la empresa cumple las condiciones del beneficio. Define el crédito de 80 UI y el precio del plan.",
                  )}
                </div>
              </div>
              {benefitDeclarationSaved ? (
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
              ) : null}
            </div>
            <div className="fx-acct-section-body">
              <FiscalBenefitDeclaration
                audience="accountant"
                idPrefix="acct-benefit"
                value={benefitDeclaration}
                onChange={setBenefitDeclaration}
              />
              <div className="fx-acct-section-cta mt-4">
                <button
                  type="button"
                  className="fx-acct-section-btn"
                  onClick={() => void handleSaveBenefitDeclaration()}
                  disabled={
                    !benefitDeclarationComplete || savingBenefitDeclaration
                  }
                >
                  {savingBenefitDeclaration ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : null}
                  {benefitDeclarationSaved
                    ? t(
                        "accountantClientOnboarding.benefit.update",
                        "Actualizar declaración",
                      )
                    : t(
                        "accountantClientOnboarding.benefit.save",
                        "Guardar declaración",
                      )}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </div>

          <div className={cn("fx-acct-section", planPaid && "is-done")}>
            <div className="fx-acct-section-head">
              <div className="fx-acct-section-ico">
                {planPaid ? (
                  <Check aria-hidden />
                ) : (
                  <CreditCard aria-hidden />
                )}
              </div>
              <div className="fx-acct-section-text">
                <div className="title">
                  {t(
                    "accountantClientOnboarding.plan.title",
                    "Plan y pago",
                  )}
                </div>
                <div className="meta">
                  {planPaid
                    ? t(
                        "accountantClientOnboarding.plan.metaAlreadyPaid",
                        "La empresa ya tiene un plan activo.",
                      )
                    : t(
                        "accountantClientOnboarding.plan.meta",
                        "Generá un enlace de pago y compartilo con la empresa por WhatsApp o correo. El pago activa la suscripción.",
                      )}
                </div>
              </div>
              {planPaid ? (
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
              ) : showCheckoutLink ? (
                <span className="fx-acct-section-status">
                  {t(
                    "accountantClientOnboarding.plan.statusWaiting",
                    "Esperando pago",
                  )}
                </span>
              ) : null}
            </div>

            {!planPaid && showCheckoutLink ? (
              <div className="fx-acct-section-body fx-acct-plan-link">
                <div className="fx-acct-plan-url" title={pendingCheckoutUrl ?? ""}>
                  {pendingCheckoutUrl}
                </div>
                <div className="fx-acct-plan-link-actions">
                  <button
                    type="button"
                    className="fx-acct-section-mark"
                    onClick={handleDiscardLink}
                  >
                    {t(
                      "accountantClientOnboarding.plan.discard",
                      "Descartar y generar otro",
                    )}
                  </button>
                  <button
                    type="button"
                    className="fx-acct-section-btn fx-acct-section-btn-secondary"
                    onClick={() => void handleCopyLink()}
                  >
                    <Copy aria-hidden />
                    {t("accountantClientOnboarding.plan.copy", "Copiar")}
                  </button>
                  <button
                    type="button"
                    className="fx-acct-section-btn"
                    onClick={handleWhatsAppShare}
                  >
                    <MessageCircle aria-hidden />
                    {t(
                      "accountantClientOnboarding.plan.shareWhatsapp",
                      "Compartir por WhatsApp",
                    )}
                  </button>
                </div>
                <div className="fx-acct-plan-link-note">
                  {t(
                    "accountantClientOnboarding.plan.linkNote",
                    "El enlace queda asociado a este cliente. Quien lo pague activa esta suscripción específicamente.",
                  )}
                </div>
              </div>
            ) : null}

            {!planPaid && !showCheckoutLink ? (
              <div className="fx-acct-section-body">
                <div className="fx-acct-plan-regime">
                  <span className="fx-acct-plan-regime-q">
                    {t(
                      "accountantClientOnboarding.plan.literalE.question",
                      "¿La empresa es Literal E (IVA mínimo)?",
                    )}
                  </span>
                  <div
                    className="fx-acct-plan-regime-opts"
                    role="group"
                    aria-label={t(
                      "accountantClientOnboarding.plan.literalE.question",
                      "¿La empresa es Literal E (IVA mínimo)?",
                    )}
                  >
                    {([true, false] as const).map((val) => {
                      const active = literalEChoice === val;
                      return (
                        <button
                          key={String(val)}
                          type="button"
                          className={cn(
                            "fx-acct-plan-regime-btn",
                            active && "is-active",
                          )}
                          aria-pressed={active}
                          onClick={() => chooseLiteralE(val)}
                        >
                          {val
                            ? t(
                                "accountantClientOnboarding.plan.literalE.yes",
                                "Sí",
                              )
                            : t(
                                "accountantClientOnboarding.plan.literalE.no",
                                "No",
                              )}
                        </button>
                      );
                    })}
                  </div>
                  <span className="fx-acct-plan-regime-help">
                    {t(
                      "accountantClientOnboarding.plan.literalE.help",
                      "Los Literal E usan el plan gratis; el resto elige un plan general.",
                    )}
                  </span>
                </div>

                {/* The declaration itself moved to its own section above, so
                    it stays reachable once a plan is resolved. Activation is
                    still GATED on it below (benefitDeclarationComplete): the
                    credit we later grant is evidenced by the declaration, and
                    a client activated without one would receive a discount
                    with nothing behind it. */}
                {!benefitDeclarationComplete ? (
                  <div className="mt-4 border-t pt-4 text-sm text-muted-foreground">
                    {t(
                      "accountantClientOnboarding.plan.benefitRequired",
                      "Completá primero la declaración del beneficio fiscal (Decreto 206/019) más arriba para poder activar un plan.",
                    )}
                  </div>
                ) : null}
                <PlanCardGrid
                  plans={visiblePlans}
                  selectedPlanId={planChoice?.planId ?? null}
                  interval={planChoice?.interval ?? "monthly"}
                  onSelectPlan={(planId) =>
                    setPlanChoice({
                      planId,
                      interval: planChoice?.interval ?? "monthly",
                    })
                  }
                  onIntervalChange={(interval) =>
                    setPlanChoice({
                      planId:
                        planChoice?.planId ?? visiblePlans[0]?.id ?? "",
                      interval,
                    })
                  }
                />
                <div className="fx-acct-section-cta">
                  <button
                    type="button"
                    className="fx-acct-section-btn"
                    onClick={() => {
                      if (selectedIsFree) {
                        void handleActivateFreePlan();
                      } else {
                        void handleGenerateLink();
                      }
                    }}
                    disabled={
                      !planChoice || generatingLink || !benefitDeclarationComplete
                    }
                  >
                    {generatingLink ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {selectedIsFree
                      ? t(
                          "accountantClientOnboarding.plan.activateFree",
                          "Activar plan gratis",
                        )
                      : t(
                          "accountantClientOnboarding.plan.generate",
                          "Generar enlace de pago",
                        )}
                    <ArrowRight aria-hidden />
                  </button>
                  {/* Say WHY the button is dead. A disabled control with no
                      explanation reads as a bug, and the declaration sits above
                      the plan cards so the reason is easy to scroll past. */}
                  {planChoice && !benefitDeclarationComplete ? (
                    <p
                      role="status"
                      className="mt-2 text-xs text-muted-foreground"
                    >
                      {t(
                        "accountantClientOnboarding.plan.benefitRequired",
                        "Completá la declaración de beneficio fiscal para activar el plan.",
                      )}
                    </p>
                  ) : null}
                </div>
                {/* Bank transfer is gated too, and by hiding rather than
                    disabling: the panel has no disabled state, and its submit
                    would otherwise no-op silently after the accountant uploaded
                    a receipt — the worst possible place to fail quietly. */}
                {!selectedIsFree && benefitDeclarationComplete ? (
                  planChoice?.interval === "annual" ? (
                    <BankTransferPanel
                      open={bankTransferOpen}
                      onToggle={() => setBankTransferOpen((o) => !o)}
                      onSubmit={handleBankTransferSubmit}
                      submitting={submittingTransfer}
                      // This wizard only ever offers the annual transfer, the
                      // same literal the amount above is derived from.
                      billingInterval="annual"
                      checkoutId={transferCheckoutId}
                      amountGrossPesos={
                        selectedPlan != null
                          ? grossWithIva(tenantPlanPrice(selectedPlan, "annual"))
                          : null
                      }
                    />
                  ) : (
                    <p className="fx-acct-bank-hint">
                      {t(
                        "accountantClientOnboarding.plan.bankTransfer.annualOnlyHint",
                        "¿Preferís pagar por transferencia bancaria? Elegí el ciclo anual.",
                      )}
                    </p>
                  )
                ) : null}
              </div>
            ) : null}
          </div>

          {!homologado && (() => {
            // Single source of truth for "can we even start the auto-flow?".
            // The DGI bot drives the e-Factura portal as the client, so every
            // earlier step the bot relies on (or that we don't want to
            // overwrite mid-flight) must be locked in first: payment, portal
            // user, certificate, CAEs, histórico de CFEs, and sucursal.
            const fiscaloConfigPrereqsOk =
              planPaid &&
              portalUserSaved &&
              certUploaded &&
              caeMarkedDone &&
              cfeMarkedDone &&
              sucursalConfirmed;
            return (
          <div
            className={cn(
              "fx-acct-section",
              fiscaloConfigDone && "is-done",
              !fiscaloConfigPrereqsOk && "is-locked",
            )}
          >
            <div className="fx-acct-section-head">
              <div className="fx-acct-section-ico">
                {fiscaloConfigDone ? (
                  <Check aria-hidden />
                ) : (
                  <Package aria-hidden />
                )}
              </div>
              <div className="fx-acct-section-text">
                <div className="title">
                  {t(
                    "accountantClientOnboarding.fiscaloConfig.title",
                    "Configuración Fiscalo en DGI",
                  )}
                </div>
                <div className="meta">
                  {t(
                    "accountantClientOnboarding.fiscaloConfig.meta",
                    "Registramos a Fiscalo como proveedor de software y sincronizamos el último IdEmisor del cliente con DGI. Una sola corrida.",
                  )}
                </div>
              </div>
              {fiscaloConfigDone ? (
                <span className="fx-acct-section-status">
                  {t("accountantClientOnboarding.status.done", "Listo")}
                </span>
              ) : fiscaloConfigManualReview ? (
                <span className="fx-acct-section-status">
                  {t(
                    "accountantClientOnboarding.status.inReview",
                    "En revisión",
                  )}
                </span>
              ) : null}
            </div>
            {!fiscaloConfigDone && !fiscaloConfigManualReview ? (
              savingFiscaloConfig ? (
                // Long-running automation (~2–5 minutes) — replace the CTA
                // row with a dedicated reassurance panel so the accountant
                // knows we're still working and doesn't bounce between tabs
                // or click again.
                <div className="fx-acct-section-body fx-acct-fiscalo-running">
                  <Loader2
                    className="fx-acct-fiscalo-running-spin animate-spin"
                    aria-hidden
                  />
                  <div className="fx-acct-fiscalo-running-text">
                    <div className="fx-acct-fiscalo-running-title">
                      {t(
                        "accountantClientOnboarding.fiscaloConfig.running.title",
                        "Configurando en DGI…",
                      )}
                    </div>
                    <div className="fx-acct-fiscalo-running-body">
                      {t(
                        "accountantClientOnboarding.fiscaloConfig.running.body",
                        "Estamos registrando a Fiscalo como proveedor de software y sincronizando el IdEmisor en el portal de DGI. Esto puede tardar hasta 5 minutos. Podés dejar esta pestaña abierta — te avisamos cuando termine.",
                      )}
                    </div>
                  </div>
                </div>
              ) : (
              <div className="fx-acct-section-body fx-acct-section-cta">
                {!planPaid ? (
                  <span className="fx-acct-section-hint">
                    {t(
                      "accountantClientOnboarding.fiscaloConfig.needsPayment",
                      "Primero generá el enlace de pago y esperá a que el cliente lo abone. La configuración en DGI se habilita al confirmar el pago.",
                    )}
                  </span>
                ) : !portalUserSaved ? (
                  <span className="fx-acct-section-hint">
                    {t(
                      "accountantClientOnboarding.fiscaloConfig.needsPortalUser",
                      "Guardá las credenciales del usuario e-Factura primero.",
                    )}
                  </span>
                ) : !certUploaded ? (
                  <span className="fx-acct-section-hint">
                    {t(
                      "accountantClientOnboarding.autoFlow.needsCert",
                      "Subí el certificado primero.",
                    )}
                  </span>
                ) : !caeMarkedDone ? (
                  <span className="fx-acct-section-hint">
                    {t(
                      "accountantClientOnboarding.fiscaloConfig.needsCaes",
                      "Subí los CAEs (o marcá el paso como hecho) antes de continuar.",
                    )}
                  </span>
                ) : !cfeMarkedDone ? (
                  <span className="fx-acct-section-hint">
                    {t(
                      "accountantClientOnboarding.fiscaloConfig.needsHistorico",
                      "Importá el histórico de CFEs (o marcá el paso como hecho) antes de continuar.",
                    )}
                  </span>
                ) : !sucursalConfirmed ? (
                  <span className="fx-acct-section-hint">
                    {t(
                      "accountantClientOnboarding.fiscaloConfig.needsSucursal",
                      "Confirmá el código de sucursal del cliente primero.",
                    )}
                  </span>
                ) : (
                  <span className="fx-acct-section-hint">
                    {t(
                      "accountantClientOnboarding.fiscaloConfig.readyHint",
                      "Última corrida automática. Puede tardar hasta 5 minutos — te avisamos cuando termine.",
                    )}
                  </span>
                )}
                <button
                  type="button"
                  className="fx-acct-section-mark"
                  onClick={() => void handleRequestManualReview()}
                  disabled={
                    !fiscaloConfigPrereqsOk ||
                    startingAutoFlow ||
                    savingFiscaloConfig
                  }
                >
                  {t(
                    "accountantClientOnboarding.fiscaloConfig.requestManual",
                    "Solicitar revisión manual",
                  )}
                </button>
                <button
                  type="button"
                  className="fx-acct-section-btn"
                  onClick={() => void handleStartFiscaloConfig()}
                  disabled={!fiscaloConfigPrereqsOk || savingFiscaloConfig}
                >
                  {t(
                    "accountantClientOnboarding.fiscaloConfig.start",
                    "Configurar ahora",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              </div>
              )
            ) : null}
          </div>
            );
          })()}

          </>
          ) : null}

          {phase === 2 ? (
            <FiscalProfilePanel
              tenant={tenant}
              fiscalProfileRef={fiscalProfileRef}
              onFiscalProfileValidityChange={setFiscalProfileValid}
            />
          ) : null}

          {phase === 3 ? (
            <FiscaloBrandingPanel
              tenant={tenant}
              publicNameInput={publicNameInput}
              setPublicNameInput={setPublicNameInput}
              savingPublicName={savingPublicName}
              onSavePublicName={handleSavePublicName}
              useSameLogo={useSameLogo}
              onUseSameLogoChange={setUseSameLogo}
              companyImageUploading={companyImageUploading}
              siteLogoUploading={siteLogoUploading}
              invoiceLogoUploading={invoiceLogoUploading}
              brandColorSaving={brandColorSaving}
              onBothLogoChange={handleBothLogoChange}
              onSiteLogoChange={handleSiteLogoChange}
              onInvoiceLogoChange={handleInvoiceLogoChange}
              onSaveBrandColor={handleSaveBrandColor}
              invoiceLogoCropSaving={invoiceLogoCropSaving}
              onSaveInvoiceLogoCrop={handleSaveInvoiceLogoCrop}
              onUploadInvoiceLogoWithCrop={handleUploadInvoiceLogoWithCrop}
            />
          ) : null}

          {phase === 4 ? (
            <TransferPanel
              tenantId={tenant.id}
              hasNonAccountantOwner={hasNonAccountantOwner}
              transferResolved={transferResolved}
              resolving={resolvingTransfer}
              onTransferInvited={() => void handleResolveTransfer("invited")}
              onSkip={() => void handleResolveTransfer("skipped")}
            />
          ) : null}

          <div className="fx-onb-actions">
            <div className="side">
              {/* Exit the wizard back to the cartera. Hidden on the final
                  done state, where the primary CTA already returns to
                  clients (and completes onboarding on the way out). */}
              {!(phase === 4 && phase4Done) ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={() => navigate("/accountant")}
                  disabled={startingAutoFlow}
                >
                  <ArrowLeft aria-hidden />
                  {t(
                    "accountantClientOnboarding.backToClients",
                    "Volver a clientes",
                  )}
                </button>
              ) : null}
            </div>
            <div className="side">
              {/* "Fase anterior" now sits next to the primary CTA (only from
                  phase 2 onward — phase 1 has no previous phase). */}
              {phase > 1 ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={() =>
                    setPhase((p) => (p > 1 ? ((p - 1) as 1 | 2 | 3 | 4) : p))
                  }
                  disabled={startingAutoFlow}
                >
                  <ArrowLeft aria-hidden />
                  {t(
                    "accountantClientOnboarding.previousPhase",
                    "Fase anterior",
                  )}
                </button>
              ) : null}
              {phase === 1 ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-primary"
                  onClick={() => setPhase(2)}
                  disabled={!phase1Done}
                  title={
                    !phase1Done
                      ? t(
                          "accountantClientOnboarding.phase1.incompleteHint",
                          "Completá la configuración de DGI antes de seguir.",
                        )
                      : undefined
                  }
                >
                  {t(
                    "accountantClientOnboarding.continueToPhase2",
                    "Seguir a Fiscalo",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              ) : phase === 2 ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-primary"
                  onClick={() => void handleConfirmFiscalProfilePanel()}
                  disabled={
                    confirmingFiscalProfilePanel || !fiscalProfileValid
                  }
                  title={
                    !fiscalProfileValid
                      ? t(
                          "accountantClientOnboarding.phase2.profileInvalidHint",
                          "Completá el perfil fiscal antes de seguir.",
                        )
                      : undefined
                  }
                >
                  {confirmingFiscalProfilePanel ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : null}
                  {t(
                    "accountantClientOnboarding.continueToPhase3",
                    "Confirmar y seguir",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              ) : phase === 3 ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-primary"
                  onClick={() => void handleConfirmFiscaloPanel()}
                  disabled={confirmingFiscaloPanel}
                >
                  {confirmingFiscaloPanel ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : null}
                  {t(
                    "accountantClientOnboarding.continueToPhase4",
                    "Confirmar y seguir",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              ) : phase4Done ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-primary"
                  onClick={() => {
                    // Belt-and-suspenders: if the resolve-transfer call
                    // never flipped onboardingCompletedAt (older versions
                    // didn't), flip it now before navigating away.
                    void completeOnboarding()
                      .catch(() => undefined)
                      .finally(() => {
                        void queryClient.invalidateQueries({
                          queryKey: ["accountant"],
                        });
                        navigate("/accountant", { replace: true });
                      });
                  }}
                >
                  {t(
                    "accountantClientOnboarding.finishWizard",
                    "Volver a clientes",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              ) : null}
            </div>
          </div>

          <AddCaeDialog
            open={caeDialogOpen}
            onOpenChange={setCaeDialogOpen}
          />
          <ImportEmittedCfeDialog
            tenantId={tenant.id}
            tenantName={displayName}
            open={cfeEmittedDialogOpen}
            onOpenChange={setCfeEmittedDialogOpen}
            importFn={importEmittedCfes}
            onSuccess={() => {
              markCfeDone();
              setCfeEmittedDialogOpen(false);
            }}
          />
          <ImportRecibidasCfeDialog
            tenantId={tenant.id}
            tenantName={displayName}
            open={cfeRecibidasDialogOpen}
            onOpenChange={setCfeRecibidasDialogOpen}
            importFn={importRecibidasCfes}
            onSuccess={() => {
              markCfeDone();
              setCfeRecibidasDialogOpen(false);
            }}
          />
        </>
      }
    />
  );
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function PhaseStepper({
  current,
  phase1Done,
  phase2Done,
  phase3Done,
  phase4Done,
  onJump,
  labels,
}: {
  current: 1 | 2 | 3 | 4;
  phase1Done: boolean;
  phase2Done: boolean;
  phase3Done: boolean;
  phase4Done: boolean;
  onJump: (phase: 1 | 2 | 3 | 4) => void;
  labels: {
    phase1: string;
    phase2: string;
    phase3: string;
    phase4: string;
  };
}): JSX.Element {
  const phases: Array<{ id: 1 | 2 | 3 | 4; label: string; done: boolean }> = [
    { id: 1, label: labels.phase1, done: phase1Done },
    { id: 2, label: labels.phase2, done: phase2Done },
    { id: 3, label: labels.phase3, done: phase3Done },
    { id: 4, label: labels.phase4, done: phase4Done },
  ];
  return (
    <div className="fx-acct-phase-stepper" role="tablist">
      {phases.map((p, idx) => (
        <button
          key={p.id}
          type="button"
          role="tab"
          aria-selected={current === p.id}
          className={cn(
            "fx-acct-phase-step",
            current === p.id && "is-current",
            p.done && "is-done",
          )}
          onClick={() => onJump(p.id)}
        >
          <span className="fx-acct-phase-step-dot">
            {p.done ? <Check aria-hidden /> : <span>{idx + 1}</span>}
          </span>
          <span className="fx-acct-phase-step-label">{p.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Phase 2: branding only (public name + logo + brand color). Used to be
 * combined with the fiscal profile in one panel; split so the fiscal
 * profile can have its own phase with the deeper IVA/IRAE configuration.
 */
function FiscaloBrandingPanel({
  tenant,
  publicNameInput,
  setPublicNameInput,
  savingPublicName,
  onSavePublicName,
  useSameLogo,
  onUseSameLogoChange,
  companyImageUploading,
  siteLogoUploading,
  invoiceLogoUploading,
  brandColorSaving,
  onBothLogoChange,
  onSiteLogoChange,
  onInvoiceLogoChange,
  onSaveBrandColor,
  invoiceLogoCropSaving,
  onSaveInvoiceLogoCrop,
  onUploadInvoiceLogoWithCrop,
}: {
  tenant: TenantCurrentResponse;
  publicNameInput: string;
  setPublicNameInput: (v: string) => void;
  savingPublicName: boolean;
  onSavePublicName: () => void;
  useSameLogo: boolean;
  onUseSameLogoChange: (v: boolean) => void;
  companyImageUploading: boolean;
  siteLogoUploading: boolean;
  invoiceLogoUploading: boolean;
  brandColorSaving: boolean;
  onBothLogoChange: (file: File | null) => void;
  onSiteLogoChange: (file: File | null) => void;
  onInvoiceLogoChange: (file: File | null) => void;
  onSaveBrandColor: (brandColor: string | null) => Promise<void>;
  invoiceLogoCropSaving: boolean;
  onSaveInvoiceLogoCrop: (payload: InvoiceLogoCropPayload) => Promise<void>;
  onUploadInvoiceLogoWithCrop: (
    file: File,
    target: "both" | "invoice",
    crop: InvoiceLogoCropPayload,
  ) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="fx-acct-fiscalo-panel">
      <div className="fx-acct-section">
        <div className="fx-acct-section-head">
          <div className="fx-acct-section-ico">
            <Sparkles aria-hidden />
          </div>
          <div className="fx-acct-section-text">
            <div className="title">
              {t(
                "accountantClientOnboarding.fiscaloConfigPanel.publicName.title",
                "Nombre público",
              )}
            </div>
            <div className="meta">
              {t(
                "accountantClientOnboarding.fiscaloConfigPanel.publicName.meta",
                "Cómo aparece la empresa en facturas y emails. La razón social la dejamos como está.",
              )}
            </div>
          </div>
        </div>
        <div className="fx-acct-section-body">
          <label className="fx-acct-form-field">
            <span className="fx-acct-form-label">
              {t(
                "accountantClientOnboarding.fiscaloConfigPanel.publicName.label",
                "Nombre",
              )}
            </span>
            <input
              type="text"
              className="fx-acct-form-input"
              value={publicNameInput}
              onChange={(e) => setPublicNameInput(e.target.value)}
              placeholder={tenant.name}
              maxLength={120}
            />
          </label>
          <div className="fx-acct-section-cta">
            <button
              type="button"
              className="fx-acct-section-btn"
              onClick={onSavePublicName}
              disabled={
                savingPublicName ||
                publicNameInput.trim() === "" ||
                publicNameInput.trim() === tenant.name
              }
            >
              {savingPublicName ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : null}
              {t(
                "accountantClientOnboarding.fiscaloConfigPanel.publicName.save",
                "Guardar nombre",
              )}
            </button>
          </div>
        </div>
      </div>

      <CompanyImageCard
        tenant={tenant}
        useSameLogo={useSameLogo}
        onUseSameLogoChange={onUseSameLogoChange}
        bothUploading={companyImageUploading}
        siteUploading={siteLogoUploading}
        invoiceUploading={invoiceLogoUploading}
        onBothLogoChange={onBothLogoChange}
        onSiteLogoChange={onSiteLogoChange}
        onInvoiceLogoChange={onInvoiceLogoChange}
        onSaveBrandColor={onSaveBrandColor}
        brandColorSaving={brandColorSaving}
        onSaveInvoiceLogoCrop={onSaveInvoiceLogoCrop}
        invoiceLogoCropSaving={invoiceLogoCropSaving}
        onUploadInvoiceLogoWithCrop={onUploadInvoiceLogoWithCrop}
      />
    </div>
  );
}

/**
 * Phase 3: just the fiscal profile editor. Validates + persists via the
 * shared FiscalProfileStep ref so handleConfirmFiscalProfilePanel can
 * gate the "Continuar" CTA on isValid().
 */
function FiscalProfilePanel({
  tenant,
  fiscalProfileRef,
  onFiscalProfileValidityChange,
}: {
  tenant: TenantCurrentResponse;
  fiscalProfileRef: React.RefObject<FiscalProfileStepRef | null>;
  onFiscalProfileValidityChange: (valid: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  // monotributo is allowed on the company entity but not on the fiscal
  // profile (FiscalCompanyType = unipersonal | sas | srl | sa). Map down
  // so the editor's company-type override doesn't crash on validation.
  const fiscalCompanyType =
    tenant.companyType && tenant.companyType !== "monotributo"
      ? tenant.companyType
      : null;
  return (
    <div className="fx-acct-fiscalo-panel">
      <div className="fx-acct-section">
        <div className="fx-acct-section-head">
          <div className="fx-acct-section-ico">
            <FileText aria-hidden />
          </div>
          <div className="fx-acct-section-text">
            <div className="title">
              {t(
                "accountantClientOnboarding.fiscaloConfigPanel.fiscalProfile.title",
                "Perfil fiscal",
              )}
            </div>
            <div className="meta">
              {t(
                "accountantClientOnboarding.fiscaloConfigPanel.fiscalProfile.meta",
                "Tipo de empresa, regímenes de IVA / IRAE, retenciones y obligaciones. Se guarda al confirmar.",
              )}
            </div>
          </div>
        </div>
        <div className="fx-acct-section-body">
          <FiscalProfileStep
            ref={fiscalProfileRef}
            industry={tenant.industry}
            activityStartDateFromCompany={tenant.activityStartDate}
            companyTypeFromCompany={fiscalCompanyType}
            onValidationChange={onFiscalProfileValidityChange}
            tenant={tenant}
            audience="accountant"
          />
        </div>
      </div>
    </div>
  );
}

function TransferPanel({
  tenantId,
  hasNonAccountantOwner,
  transferResolved,
  resolving,
  onTransferInvited,
  onSkip,
}: {
  tenantId: string;
  hasNonAccountantOwner: boolean;
  transferResolved: boolean;
  resolving: boolean;
  onTransferInvited: () => void;
  onSkip: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="fx-acct-transfer-panel">
      <div className="fx-acct-section">
        <div className="fx-acct-section-head">
          <div className="fx-acct-section-ico">
            <KeyRound aria-hidden />
          </div>
          <div className="fx-acct-section-text">
            <div className="title">
              {t(
                "accountantClientOnboarding.transfer.title",
                "Transferir propiedad",
              )}
            </div>
            <div className="meta">
              {hasNonAccountantOwner
                ? t(
                    "accountantClientOnboarding.transfer.metaHasOwner",
                    "Ya hay un propietario distinto al contador en esta empresa. Nada que hacer acá.",
                  )
                : t(
                    "accountantClientOnboarding.transfer.meta",
                    "Elegí cómo termina esta empresa: te quedás como propietario (si el cliente delega en vos) o invitás al titular real para transferirle la propiedad.",
                  )}
            </div>
          </div>
          {transferResolved ? (
            <span className="fx-acct-section-status">
              {t("accountantClientOnboarding.status.done", "Listo")}
            </span>
          ) : null}
        </div>
        {!hasNonAccountantOwner ? (
          <div className="fx-acct-section-body flex flex-col gap-3">
            <KeepOwnershipCard
              onConfirm={onSkip}
              disabled={resolving}
            />
            <TransferOwnershipCard
              tenantId={tenantId}
              onTransferRequested={onTransferInvited}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Sister option to TransferOwnershipCard: the accountant declares they're
 * staying on as the owner. Used when the client delegates operation (the
 * accountant runs invoicing/DGI for them) so the onboarding can complete
 * without inviting a different human as owner.
 */
function KeepOwnershipCard({
  onConfirm,
  disabled,
}: {
  onConfirm: () => void;
  disabled: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--fx-accent-50)] text-[var(--fx-accent-700)]">
            <UserCheck className="h-4 w-4" aria-hidden />
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-[var(--fx-ink-900)]">
              {t(
                "accountantClientOnboarding.keepOwnership.title",
                "Me quedo como propietario",
              )}
            </h3>
            <p className="text-xs text-[var(--fx-ink-600)]">
              {t(
                "accountantClientOnboarding.keepOwnership.description",
                "Si el cliente delega en vos (facturás y operás por ellos), seguís como propietario de esta empresa.",
              )}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onConfirm}
          disabled={disabled}
          className="shrink-0"
        >
          {disabled ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          ) : null}
          {t(
            "accountantClientOnboarding.keepOwnership.confirm",
            "Confirmar",
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

